import React, { useState, useEffect, useCallback, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Users, Star, Menu, X, Phone, Mail, ChevronDown, ChevronLeft, ChevronRight, Anchor, Wifi, Car, Utensils, Waves, Crown, Shield, Clock, Calendar, ArrowRight, ZoomIn, Download, Share2, MessageCircle } from "lucide-react";
import DestinationPage from './components/DestinationPage';
import ServicePage from './components/ServicePage';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { setupErrorHandling, ErrorBoundary, detectProblematicExtensions } from './utils/errorHandler';
import { throttle } from './utils/performance';

// WhatsApp Floating Bar Component - PRIVIA Design
const WhatsAppBar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const phoneNumber = "+17868400745"; // Christopher's number
  const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=Hello! I'm interested in yacht charter services.`;

  // Handle scroll effect like navigation
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppClick = () => {
    window.open(whatsappUrl, '_blank');
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className={`transition-all duration-500 ease-out ${isExpanded ? 'w-80' : 'w-auto'}`}>
        <div className={`rounded-sm shadow-2xl backdrop-blur-md ring-1 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 text-charcoal-900 border-2 border-charcoal-200/50 ring-black/5' 
            : 'bg-gradient-to-r from-charcoal-900/98 to-charcoal-800/98 text-white border-2 border-charcoal-700/50 ring-black/10'
        }`}>
          {!isExpanded ? (
            // Collapsed State - Elegant Bar
            <button
              onClick={toggleExpand}
              className={`flex items-center space-x-3 px-6 py-4 transition-all duration-300 group rounded-sm ${
                isScrolled 
                  ? 'hover:bg-charcoal-100/50' 
                  : 'hover:bg-charcoal-800/90'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full animate-pulse opacity-60 ${
                  isScrolled ? 'bg-charcoal-900' : 'bg-white'
                }`}></div>
                <MessageCircle className={`w-5 h-5 transition-colors drop-shadow-sm ${
                  isScrolled 
                    ? 'text-charcoal-900 group-hover:text-charcoal-700' 
                    : 'text-white group-hover:text-white'
                }`} />
                <span className={`text-sm font-medium tracking-wide uppercase transition-colors drop-shadow-sm ${
                  isScrolled 
                    ? 'text-charcoal-900 group-hover:text-charcoal-700' 
                    : 'text-white group-hover:text-white'
                }`}>
                  Talk to a Specialist
                </span>
              </div>
              <ChevronLeft className={`w-4 h-4 transition-all transform group-hover:-translate-x-1 drop-shadow-sm ${
                isScrolled 
                  ? 'text-charcoal-600 group-hover:text-charcoal-700' 
                  : 'text-white/80 group-hover:text-white'
              }`} />
            </button>
          ) : (
            // Expanded State - Full Content
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse opacity-60 ${
                    isScrolled ? 'bg-charcoal-900' : 'bg-white'
                  }`}></div>
                  <span className={`text-xs font-light tracking-[0.2em] uppercase ${
                    isScrolled ? 'text-charcoal-600' : 'text-white/70'
                  }`}>Available Now</span>
                </div>
                <button 
                  onClick={toggleExpand}
                  className={`transition-colors p-1 ${
                    isScrolled 
                      ? 'text-charcoal-500 hover:text-charcoal-700' 
                      : 'text-white/50 hover:text-white/80'
                  }`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="mb-6">
                <h4 className={`text-lg font-light mb-2 tracking-wide ${
                  isScrolled ? 'text-charcoal-900' : 'text-white'
                }`}>
                  Talk to a Yacht Charter Specialist
                </h4>
                <p className={`text-sm font-light leading-relaxed ${
                  isScrolled ? 'text-charcoal-600' : 'text-white/60'
                }`}>
                  Get expert advice on your perfect yacht experience from Christopher Warren
                </p>
              </div>
              
              <button
                onClick={handleWhatsAppClick}
                className={`w-full rounded-sm py-4 px-4 flex items-center justify-center space-x-3 transition-all duration-300 group backdrop-blur-sm border ${
                  isScrolled 
                    ? 'bg-charcoal-900/5 hover:bg-charcoal-900/10 border-charcoal-900/10 hover:border-charcoal-900/20' 
                    : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                }`}
              >
                <MessageCircle className={`w-5 h-5 group-hover:scale-110 transition-all ${
                  isScrolled 
                    ? 'text-charcoal-700 group-hover:text-charcoal-900' 
                    : 'text-white/80 group-hover:text-white'
                }`} />
                <span className={`text-sm font-light tracking-wide ${
                  isScrolled 
                    ? 'text-charcoal-800 group-hover:text-charcoal-900' 
                    : 'text-white/90 group-hover:text-white'
                }`}>
                  Start WhatsApp Conversation
                </span>
                <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-all ${
                  isScrolled 
                    ? 'text-charcoal-600 group-hover:text-charcoal-900' 
                    : 'text-white/60 group-hover:text-white'
                }`} />
              </button>
              
              <div className={`mt-4 pt-4 border-t ${
                isScrolled ? 'border-charcoal-900/10' : 'border-white/10'
              }`}>
                <div className={`flex items-center justify-center space-x-2 text-xs font-light ${
                  isScrolled ? 'text-charcoal-500' : 'text-white/50'
                }`}>
                  <Phone className="w-3 h-3" />
                  <span>+1 786 840 0745</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

console.log('🔧 API CONFIGURATION:');
console.log('📡 BACKEND_URL:', BACKEND_URL);
console.log('🌐 API URL:', API);
console.log('🔑 REACT_APP_BACKEND_URL env var:', process.env.REACT_APP_BACKEND_URL);

// Cache simple pour éviter les requêtes répétées
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const cachedFetch = async (url) => {
  const now = Date.now();
  const cached = cache.get(url);
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    console.log('📦 CACHE HIT:', url);
    return cached.data;
  }
  
  console.log('🌐 CACHE MISS - Fetching:', url);
  const response = await axios.get(url);
  cache.set(url, { data: response.data, timestamp: now });
  return response.data;
};

// Composant Image avec lazy loading
const LazyImage = ({ src, alt, className, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={className} {...props}>
      {isInView && (
        <>
          {!isLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={src}
            alt={alt}
            className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
            {...props}
          />
        </>
      )}
    </div>
  );
};

// Photo Gallery Modal Component - Ultra Refined with REAL yacht images
const PhotoGalleryModal = ({ isOpen, onClose, yacht, initialPhotoIndex = 0 }) => {
  const [currentPhoto, setCurrentPhoto] = useState(initialPhotoIndex);
  const [isZoomed, setIsZoomed] = useState(false);

  // Use REAL yacht images from the database
  const photos = yacht?.images?.all ? yacht.images.all.map((img, index) => ({
    url: img.url,
    title: img.alt || `${yacht.name} - Image ${index + 1}`,
    description: `${yacht.name} - ${img.type || 'Yacht'} view`,
    category: img.type || 'Yacht'
  })) : [];

  const currentPhotoData = photos[currentPhoto] || photos[0];

  const nextPhoto = () => {
    setCurrentPhoto(prev => prev === photos.length - 1 ? 0 : prev + 1);
    setIsZoomed(false);
  };

  const prevPhoto = () => {
    setCurrentPhoto(prev => prev === 0 ? photos.length - 1 : prev - 1);
    setIsZoomed(false);
  };

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'ArrowRight') nextPhoto();
    if (e.key === 'ArrowLeft') prevPhoto();
    if (e.key === 'Escape') onClose();
  }, [photos.length, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyPress]);

  useEffect(() => {
    setCurrentPhoto(initialPhotoIndex);
  }, [initialPhotoIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-charcoal-900/95 backdrop-blur-lg">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-6">
        <div className="flex justify-between items-center">
          <div className="text-white">
            <h1 className="text-2xl font-light tracking-wide">
              PRIVIA<span className="font-thin">CHARTER</span>
            </h1>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-white text-sm font-light">
              {currentPhoto + 1} / {photos.length}
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 bg-white/20 backdrop-blur-sm border-2 border-white/40 rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:border-white/60 transition-all shadow-lg"
              title="Close Gallery"
            >
              <X className="w-6 h-6 stroke-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Image - Full Size */}
      <div className="flex items-center justify-center h-full p-4">
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={currentPhotoData.url}
            alt={currentPhotoData.title}
            className={`transition-transform duration-500 ${
              isZoomed 
                ? 'w-full h-full object-contain scale-100 cursor-zoom-out' 
                : 'max-w-[95vw] max-h-[85vh] object-contain cursor-zoom-in'
            }`}
            onClick={() => setIsZoomed(!isZoomed)}
          />
          
          {/* Navigation Arrows - Enhanced */}
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevPhoto();
                }}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/20 backdrop-blur-sm border-2 border-white/40 rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:border-white/60 transition-all shadow-lg z-10"
                title="Previous Image"
              >
                <ChevronLeft className="w-7 h-7 stroke-2" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextPhoto();
                }}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/20 backdrop-blur-sm border-2 border-white/40 rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:border-white/60 transition-all shadow-lg z-10"
                title="Next Image"
              >
                <ChevronRight className="w-7 h-7 stroke-2" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bottom Info Panel */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="text-white">
              <div className="flex items-center space-x-3 mb-1">
                <span className="px-2 py-1 bg-white/15 text-xs font-light tracking-wide uppercase rounded">
                  {currentPhotoData.category}
                </span>
              </div>
              <h3 className="text-lg font-light tracking-wide mb-1">
                {currentPhotoData.title}
              </h3>
              <p className="text-xs font-light text-white/60">
                {currentPhotoData.description}
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsZoomed(!isZoomed)}
                className="w-8 h-8 bg-white/5 border border-white/15 rounded-full flex items-center justify-center text-white hover:bg-white/15 transition-all"
              >
                <ZoomIn className="w-3 h-3" />
              </button>
              <button className="w-8 h-8 bg-white/5 border border-white/15 rounded-full flex items-center justify-center text-white hover:bg-white/15 transition-all">
                <Share2 className="w-3 h-3" />
              </button>
              <button className="w-8 h-8 bg-white/5 border border-white/15 rounded-full flex items-center justify-center text-white hover:bg-white/15 transition-all">
                <Download className="w-3 h-3" />
              </button>
            </div>
          </div>
          
          {/* Thumbnail Navigation */}
          <div className="flex space-x-1 overflow-x-auto">
            {photos.map((photo, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentPhoto(index);
                  setIsZoomed(false);
                }}
                className={`flex-shrink-0 w-12 h-8 overflow-hidden rounded border transition-all ${
                  index === currentPhoto 
                    ? 'border-white/80' 
                    : 'border-white/20 hover:border-white/50'
                }`}
              >
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Header Component - Refined and minimal
const Header = ({ onMenuClick, isMenuOpen, isTransparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const shouldBeTransparent = isTransparent && !isScrolled;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${shouldBeTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-lg shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <h1 
              className={`text-2xl font-light tracking-wide transition-colors cursor-pointer hover:opacity-80 ${shouldBeTransparent ? 'text-white' : 'text-charcoal-900'}`}
              onClick={() => navigate('/')}
            >
              PRIVIA<span className="font-thin">CHARTER</span>
            </h1>
          </div>
          
          <nav className="hidden lg:flex space-x-12">
            <a href="/escapes" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>
              {t.nav.destinations.toUpperCase()}
            </a>
            <a href="/collection" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>
              {t.nav.collection.toUpperCase()}
            </a>
            <a href="/experience" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>
              EXPERIENCE
            </a>
            <a href="/contact" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>
              {t.nav.contact.toUpperCase()}
            </a>
          </nav>
          
          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:+17868400745" className={`flex items-center text-xs font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>
              <Phone className="w-3 h-3 mr-2" />
              +1 786 840 0745
            </a>
          </div>
          
          <button 
            className={`lg:hidden transition-colors ${shouldBeTransparent ? 'text-white' : 'text-charcoal-900'}`}
            onClick={onMenuClick}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </header>
  );
};

// Image Gallery Component for Detail Page
const ImageGallery = ({ images, yachtName }) => {
  const [currentImage, setCurrentImage] = useState(0);
  
  const allImages = [
    images.main,
    images.interior1,
    images.interior2,
    images.exterior1,
    images.exterior2
  ].filter(Boolean);

  return (
    <div className="relative h-screen w-full">
      <div className="absolute inset-0">
        <img 
          src={allImages[currentImage]} 
          alt={`${yachtName} - Image ${currentImage + 1}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20"></div>
      </div>
      
      {/* Navigation */}
      <button 
        onClick={() => setCurrentImage(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      <button 
        onClick={() => setCurrentImage(prev => prev === allImages.length - 1 ? 0 : prev + 1)}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      
      {/* Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {allImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImage(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentImage ? 'bg-white' : 'bg-white/40'
            }`}
          />
        ))}
      </div>
      
      {/* Overlay Content */}
      <div className="absolute bottom-20 left-8 text-white">
        <p className="text-xs font-light tracking-[0.3em] mb-2 uppercase opacity-80">
          {currentImage === 0 ? 'Vue Principale' : 
           currentImage === 1 ? 'Intérieur Luxueux' :
           currentImage === 2 ? 'Salon Principal' :
           currentImage === 3 ? 'Vue Extérieure' : 'Pont Supérieur'}
        </p>
        <h1 className="text-4xl lg:text-6xl font-thin tracking-tight">{yachtName}</h1>
      </div>
    </div>
  );
};

// Yacht Detail Page Component
const YachtDetail = ({ onBookNow }) => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [yacht, setYacht] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [initialPhotoIndex, setInitialPhotoIndex] = useState(0);

  useEffect(() => {
    fetchYachtDetail();
  }, [id]);

  // Redirect to SEO URL if accessed via old URL format
  useEffect(() => {
    if (yacht && yacht.seo_url && !slug) {
      // Replace current URL with SEO-friendly version
      navigate(yacht.seo_url, { replace: true });
    }
  }, [yacht, slug, navigate]);

  const fetchYachtDetail = async () => {
    try {
      console.log('🚀 YACHT DETAIL: Retrieving complete data for yacht', id);
      const response = await axios.get(`${API}/yachts/${id}`);
      const yachtData = response.data;
      
      console.log('✅ YACHT DETAIL: Data received:', {
        name: yachtData.name,
        images: yachtData.images?.all?.length || 0,
        features: yachtData.luxury_features?.length || 0,
        destinations: yachtData.cruising_regions?.all_destinations?.length || 0,
        rates: yachtData.charter_rates?.length || 0
      });
      
      // Data is already complete from enriched API
      // Add some data for compatibility with current interface
      const enhancedYacht = {
        ...yachtData,
        // Ensure compatibility with existing interface
        pricing: {
          daily: yachtData.price_per_day || yachtData.pricing?.daily || 25000,
          weekly: yachtData.pricing?.weekly_eur || (yachtData.price_per_day || 25000) * 7,
          extras: {
            fuel: "Consumption based",
            crew_tips: "15-20% recommended", 
            delivery: "On request"
          }
        },
        captain: "Experienced Captain",
        chef: "Professional Chef",
        crew_size: yachtData.crew
      };
      
      setYacht(enhancedYacht);
      setLoading(false);
    } catch (error) {
      console.error('❌ YACHT DETAIL: Error retrieving details:', error);
      
      // PRODUCTION: Pas de données de fallback - afficher une erreur propre
      
      setLoading(false);
    }
  };

  const getShipyardByType = (type) => {
    const shipyards = {
      "Motor Yacht": "Azimut",
      "Super Yacht": "Lürssen",
      "Party Yacht": "Sunseeker",
      "Sailing Yacht": "Perini Navi",
      "Sport Yacht": "Ferretti"
    };
    return shipyards[type] || "Prestige";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border border-charcoal-300 border-t-charcoal-900 rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-sm font-light text-charcoal-600 tracking-wide">Chargement...</div>
        </div>
      </div>
    );
  }

  if (!yacht) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-light text-charcoal-900 mb-4">Yacht non trouvé</h1>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all"
          >
            Retour à la Collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Gallery */}
      <ImageGallery images={yacht.images} yachtName={yacht.name} />
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        {/* Quick Info Bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20 p-8 bg-warm-gray-50 rounded-sm">
          <div className="text-center">
            <div className="text-2xl font-light text-charcoal-900 mb-1">{yacht.specifications?.length || yacht.specifications?.length_display || 'N/A'}</div>
            <div className="text-xs font-light text-charcoal-500 tracking-wide uppercase">Length</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-charcoal-900 mb-1">{yacht.guests}</div>
            <div className="text-xs font-light text-charcoal-500 tracking-wide uppercase">Guests</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-light text-charcoal-900 mb-1">{yacht.crew}</div>
            <div className="text-xs font-light text-charcoal-500 tracking-wide uppercase">Crew</div>
          </div>
          <div className="text-center">
            {(() => {
              const priceInfo = formatCharterPrice(yacht.charter_rates);
              return (
                <>
                  <div className="text-2xl font-light text-charcoal-900 mb-1">{priceInfo.price}</div>
                  <div className="text-xs font-light text-charcoal-500 tracking-wide uppercase">Per Week</div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-16">
            
            {/* Description */}
            <section>
              <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">The Art of Travel</h2>
              <h3 className="text-3xl font-thin text-charcoal-900 mb-6 tracking-tight">An Exceptional Experience</h3>
              <p className="text-lg font-light text-charcoal-700 leading-relaxed mb-6">
                {yacht.description || yacht.main_quote || `Discover the ${yacht.name}, a marvel of naval engineering.`}
              </p>
              
              {/* Real Accommodation Description */}
              {yacht.accommodation_description && (
                <div className="bg-warm-gray-50 p-6 rounded-sm border border-warm-gray-200 mb-6">
                  <h4 className="text-sm font-light text-charcoal-500 mb-3 uppercase tracking-wide">Accommodation</h4>
                  <p className="text-base font-light text-charcoal-700 leading-relaxed">
                    {yacht.accommodation_description}
                  </p>
                </div>
              )}
              
              {/* Yacht Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {yacht.subtitle && (
                  <div>
                    <h4 className="text-sm font-light text-charcoal-500 mb-2 uppercase tracking-wide">Présentation</h4>
                    <p className="text-base font-light text-charcoal-700">{yacht.subtitle}</p>
                  </div>
                )}
                
                {(yacht.cabins || yacht.total_suites) && (
                  <div>
                    <h4 className="text-sm font-light text-charcoal-500 mb-2 uppercase tracking-wide">Configuration</h4>
                    <p className="text-base font-light text-charcoal-700">
                      {yacht.total_suites ? `${yacht.total_suites} suites` : `${yacht.cabins} cabines`}
                      {yacht.max_guests && yacht.max_guests !== yacht.guests && (
                        <span className="text-charcoal-500"> • Maximum {yacht.max_guests} invités</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Luxury Features */}
            <section>
              <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Onboard Excellence</h2>
              <h3 className="text-3xl font-thin text-charcoal-900 mb-8 tracking-tight">Luxury Amenities & Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {yacht.luxury_features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-warm-gray-50 rounded-sm">
                    <div className="w-2 h-2 bg-charcoal-400 rounded-full"></div>
                    <span className="text-sm font-light text-charcoal-700">{feature}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Specifications */}
            <section>
              <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Naval Engineering</h2>
              <h3 className="text-3xl font-thin text-charcoal-900 mb-8 tracking-tight">Performance & Craftsmanship</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Length</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.length_display || yacht.specifications?.length || 'N/A'}</div>
                  </div>
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Beam</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.beam || 'N/A'}</div>
                  </div>
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Draft</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.draft || 'N/A'}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Max Speed</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.maximum_speed || 'N/A'}</div>
                  </div>
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Cruising Speed</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.cruising_speed || 'N/A'}</div>
                  </div>
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Range</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.range_nautical_miles || 'N/A'}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Builder</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.builder || 'N/A'}</div>
                  </div>
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Year</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.built_year || 'N/A'}</div>
                  </div>
                  <div className="pb-3 border-b border-warm-gray-200">
                    <div className="text-sm font-light text-charcoal-500 mb-1">Fuel Capacity</div>
                    <div className="text-base font-light text-charcoal-900">{yacht.specifications?.fuel_capacity || 'N/A'}</div>
                  </div>
                </div>
              </div>
              
              {/* Additional technical specifications */}
              {(yacht.specifications?.engines || yacht.specifications?.hull_material || yacht.specifications?.exterior_designer) && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {yacht.specifications?.engines && (
                    <div className="bg-warm-gray-50 p-6 rounded-sm">
                      <h4 className="text-sm font-light text-charcoal-500 mb-2 uppercase tracking-wide">Engines</h4>
                      <p className="text-base font-light text-charcoal-900">{yacht.specifications.engines}</p>
                    </div>
                  )}
                  {yacht.specifications?.hull_material && (
                    <div className="bg-warm-gray-50 p-6 rounded-sm">
                      <h4 className="text-sm font-light text-charcoal-500 mb-2 uppercase tracking-wide">Materials</h4>
                      <p className="text-base font-light text-charcoal-900">
                        Hull: {yacht.specifications.hull_material}
                        {yacht.specifications?.superstructure_material && (
                          <><br />Superstructure: {yacht.specifications.superstructure_material}</>
                        )}
                      </p>
                    </div>
                  )}
                  {yacht.specifications?.exterior_designer && (
                    <div className="bg-warm-gray-50 p-6 rounded-sm">
                      <h4 className="text-sm font-light text-charcoal-500 mb-2 uppercase tracking-wide">Design</h4>
                      <p className="text-base font-light text-charcoal-900">
                        Exterior: {yacht.specifications.exterior_designer}
                        {yacht.specifications?.interior_design && (
                          <><br />Interior: {yacht.specifications.interior_design}</>
                        )}
                      </p>
                    </div>
                  )}
                  {yacht.specifications?.gross_tonnage && (
                    <div className="bg-warm-gray-50 p-6 rounded-sm">
                      <h4 className="text-sm font-light text-charcoal-500 mb-2 uppercase tracking-wide">Tonnage</h4>
                      <p className="text-base font-light text-charcoal-900">{yacht.specifications.gross_tonnage}</p>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Real Charter Rates & Destinations */}
            <section>
              <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Pricing & Destinations</h2>
              <h3 className="text-3xl font-thin text-charcoal-900 mb-8 tracking-tight">Seasons & Cruising Regions</h3>
              
              {/* Real Charter Rates */}
              {yacht.charter_rates && yacht.charter_rates.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                  {yacht.charter_rates.map((rate, index) => (
                    <div key={index} className={`p-8 rounded-sm border ${
                      rate.season === 'summer' 
                        ? 'bg-gradient-to-br from-amber-50 to-orange-100 border-amber-200' 
                        : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                    }`}>
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xl font-light text-charcoal-900 tracking-wide uppercase">
                            {rate.season === 'summer' ? 'SUMMER' : 'WINTER'}
                          </h4>
                          <div className="text-right">
                            <div className="text-2xl font-thin text-charcoal-900">
                              {(() => {
                                // Priorité USD, fallback EUR
                                if (rate.rate_usd) return rate.rate_usd;
                                if (rate.rate_eur) return rate.rate_eur;
                                return 'On Request';
                              })()}
                            </div>
                            {rate.period && (
                              <div className="text-xs font-light text-charcoal-500">{rate.period}</div>
                            )}
                          </div>
                        </div>
                        
                        {rate.note && (
                          <div className="text-sm font-light text-charcoal-600 mb-4">{rate.note}</div>
                        )}
                        
                        {/* High/Low Season Rates */}
                        {(rate.high_season_eur || rate.low_season_eur) && (
                          <div className="space-y-2">
                            {rate.high_season_eur && (
                              <div className="flex justify-between text-sm">
                                <span className="font-light text-charcoal-500">High season:</span>
                                <span className="font-light text-charcoal-900">{rate.high_season_eur}</span>
                              </div>
                            )}
                            {rate.low_season_eur && (
                              <div className="flex justify-between text-sm">
                                <span className="font-light text-charcoal-500">Low season:</span>
                                <span className="font-light text-charcoal-900">{rate.low_season_eur}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-warm-gray-50 p-8 rounded-sm border border-warm-gray-200 mb-8">
                  <div className="text-center">
                    <h4 className="text-xl font-light text-charcoal-900 mb-2">Custom Pricing</h4>
                    <p className="text-base font-light text-charcoal-600">
                      Contact our advisors for a personalized quote according to your dates and destinations.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Real Cruising Destinations */}
              {yacht.cruising_regions && yacht.cruising_regions.all_destinations && yacht.cruising_regions.all_destinations.length > 0 && (
                <div className="space-y-6">
                  <h4 className="text-lg font-light text-charcoal-900 tracking-wide">Available Destinations</h4>
                  
                  {/* Mediterranean */}
                  {yacht.cruising_regions.mediterranean && yacht.cruising_regions.mediterranean.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-sm border border-blue-200">
                      <h5 className="text-sm font-light text-charcoal-500 mb-3 uppercase tracking-wide">Mediterranean</h5>
                      <div className="flex flex-wrap gap-2">
                        {yacht.cruising_regions.mediterranean.map((region, index) => (
                          <span key={index} className={`px-3 py-1 text-xs font-light rounded-full ${
                            region.is_hotspot 
                              ? 'bg-blue-200 text-blue-900 border border-blue-300' 
                              : 'bg-white/60 backdrop-blur-sm text-charcoal-700'
                          }`}>
                            {region.country_destination}
                            {region.is_hotspot && ' ⭐'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Caribbean */}
                  {yacht.cruising_regions.caribbean && yacht.cruising_regions.caribbean.length > 0 && (
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-sm border border-emerald-200">
                      <h5 className="text-sm font-light text-charcoal-500 mb-3 uppercase tracking-wide">Caribbean</h5>
                      <div className="flex flex-wrap gap-2">
                        {yacht.cruising_regions.caribbean.map((region, index) => (
                          <span key={index} className={`px-3 py-1 text-xs font-light rounded-full ${
                            region.is_hotspot 
                              ? 'bg-emerald-200 text-emerald-900 border border-emerald-300' 
                              : 'bg-white/60 backdrop-blur-sm text-charcoal-700'
                          }`}>
                            {region.country_destination}
                            {region.is_hotspot && ' ⭐'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Other Regions */}
                  {yacht.cruising_regions.other && yacht.cruising_regions.other.length > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-sm border border-purple-200">
                      <h5 className="text-sm font-light text-charcoal-500 mb-3 uppercase tracking-wide">Other Regions</h5>
                      <div className="flex flex-wrap gap-2">
                        {yacht.cruising_regions.other.map((region, index) => (
                          <span key={index} className={`px-3 py-1 text-xs font-light rounded-full ${
                            region.is_hotspot 
                              ? 'bg-purple-200 text-purple-900 border border-purple-300' 
                              : 'bg-white/60 backdrop-blur-sm text-charcoal-700'
                          }`}>
                            {region.country_destination}
                            {region.is_hotspot && ' ⭐'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Real Photo Gallery */}
            <section>
              <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Photo Gallery</h2>
              <h3 className="text-3xl font-thin text-charcoal-900 mb-8 tracking-tight">Discover Every Detail</h3>
              
              {yacht.images && yacht.images.all && yacht.images.all.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Main Image */}
                  {yacht.images.main && (
                    <div className="md:col-span-2 lg:col-span-2">
                      <div className="text-xs font-light tracking-wide text-charcoal-500 mb-3 uppercase">Main Image</div>
                      <div className="relative group cursor-pointer overflow-hidden rounded-sm" onClick={() => {
                        setInitialPhotoIndex(0);
                        setIsGalleryOpen(true);
                      }}>
                        <img 
                          src={yacht.images.main} 
                          alt={yacht.name}
                          className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute bottom-3 left-3 text-white text-sm font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {yacht.name}
                        </div>
                        <div className="absolute top-3 right-3 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <ZoomIn className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Exterior Images */}
                  {yacht.images.exterior && yacht.images.exterior.length > 0 && (
                    <div className="space-y-4">
                      <div className="text-xs font-light tracking-wide text-charcoal-500 mb-3 uppercase">
                        Exterior ({yacht.images.exterior.length})
                      </div>
                      {yacht.images.exterior.slice(0, 4).map((imageUrl, index) => (
                        <div key={index} className="relative group cursor-pointer overflow-hidden rounded-sm" onClick={() => {
                          const exteriorStartIndex = yacht.images.all.findIndex(img => img.url === imageUrl);
                          setInitialPhotoIndex(exteriorStartIndex >= 0 ? exteriorStartIndex : index + 1);
                          setIsGalleryOpen(true);
                        }}>
                          <img 
                            src={imageUrl} 
                            alt={`Exterior ${index + 1}`}
                            className="w-full h-32 object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute bottom-2 left-2 text-white text-xs font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            Exterior {index + 1}
                          </div>
                          <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <ZoomIn className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      ))}
                      {yacht.images.exterior.length > 4 && (
                        <div className="text-center">
                          <button 
                            onClick={() => {
                              setInitialPhotoIndex(1);
                              setIsGalleryOpen(true);
                            }}
                            className="text-xs font-light text-charcoal-500 hover:text-charcoal-700 transition-colors"
                          >
                            +{yacht.images.exterior.length - 4} more exterior photos
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Interior Images */}
                  {yacht.images.interior && yacht.images.interior.length > 0 && (
                    <div className="space-y-4">
                      <div className="text-xs font-light tracking-wide text-charcoal-500 mb-3 uppercase">
                        Interior ({yacht.images.interior.length})
                      </div>
                      {yacht.images.interior.slice(0, 3).map((imageUrl, index) => (
                        <div key={index} className="relative group cursor-pointer overflow-hidden rounded-sm" onClick={() => {
                          const interiorStartIndex = yacht.images.all.findIndex(img => img.url === imageUrl);
                          setInitialPhotoIndex(interiorStartIndex >= 0 ? interiorStartIndex : index);
                          setIsGalleryOpen(true);
                        }}>
                          <img 
                            src={imageUrl} 
                            alt={`Interior ${index + 1}`}
                            className="w-full h-32 object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute bottom-2 left-2 text-white text-xs font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            Interior {index + 1}
                          </div>
                          <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <ZoomIn className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      ))}
                      {yacht.images.interior.length > 3 && (
                        <div className="text-center">
                          <button 
                            onClick={() => {
                              const firstInteriorIndex = yacht.images.all.findIndex(img => img.type === 'interior');
                              setInitialPhotoIndex(firstInteriorIndex >= 0 ? firstInteriorIndex : 0);
                              setIsGalleryOpen(true);
                            }}
                            className="text-xs font-light text-charcoal-500 hover:text-charcoal-700 transition-colors"
                          >
                            +{yacht.images.interior.length - 3} more interior photos
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Lifestyle Images */}
                  {yacht.images.lifestyle && yacht.images.lifestyle.length > 0 && (
                    <div className="space-y-4">
                      <div className="text-xs font-light tracking-wide text-charcoal-500 mb-3 uppercase">
                        Lifestyle ({yacht.images.lifestyle.length})
                      </div>
                      {yacht.images.lifestyle.slice(0, 2).map((imageUrl, index) => (
                        <div key={index} className="relative group cursor-pointer overflow-hidden rounded-sm" onClick={() => {
                          const lifestyleStartIndex = yacht.images.all.findIndex(img => img.url === imageUrl);
                          setInitialPhotoIndex(lifestyleStartIndex >= 0 ? lifestyleStartIndex : index);
                          setIsGalleryOpen(true);
                        }}>
                          <img 
                            src={imageUrl} 
                            alt={`Lifestyle ${index + 1}`}
                            className="w-full h-32 object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute bottom-2 left-2 text-white text-xs font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            Lifestyle {index + 1}
                          </div>
                          <div className="absolute top-2 right-2 w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <ZoomIn className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-warm-gray-50 p-8 rounded-sm border border-warm-gray-200">
                  <div className="text-center">
                    <h4 className="text-lg font-light text-charcoal-900 mb-2">Galerie Bientôt Disponible</h4>
                    <p className="text-base font-light text-charcoal-600">
                      Les photos de ce yacht seront bientôt disponibles. Contactez-nous pour plus d'informations.
                    </p>
                  </div>
                </div>
              )}
              
              {/* View All Photos Button */}
              {yacht.images && yacht.images.all && yacht.images.all.length > 6 && (
                <div className="text-center mt-8">
                  <button 
                    onClick={() => {
                      setInitialPhotoIndex(0);
                      setIsGalleryOpen(true);
                    }}
                    className="px-8 py-3 border border-charcoal-300 text-charcoal-700 text-sm font-light tracking-wide hover:bg-charcoal-50 transition-all uppercase"
                  >
                    View All Photos ({yacht.images.all.length})
                  </button>
                </div>
              )}
            </section>

            {/* Crew - Hidden as we don't have real crew information */}
            {/* 
            <section>
              <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Professional Crew</h2>
              <h3 className="text-3xl font-thin text-charcoal-900 mb-8 tracking-tight">Exceptional Service</h3>
              <div className="bg-warm-gray-50 p-8 rounded-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <Crown className="w-5 h-5 text-charcoal-400" />
                      <span className="text-sm font-light text-charcoal-500 uppercase tracking-wide">Captain</span>
                    </div>
                    <div className="text-lg font-light text-charcoal-900">{yacht.captain}</div>
                    <p className="text-sm font-light text-charcoal-600 mt-2">
                      Over 15 years of offshore navigation experience and perfect knowledge of the Mediterranean.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <Utensils className="w-5 h-5 text-charcoal-400" />
                      <span className="text-sm font-light text-charcoal-500 uppercase tracking-wide">Chef</span>
                    </div>
                    <div className="text-lg font-light text-charcoal-900">{yacht.chef}</div>
                    <p className="text-sm font-light text-charcoal-600 mt-2">
                      French and international gourmet cuisine, premium local products.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            */}
          </div>

          {/* Right Column - Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-8">
              
              {/* Pricing Card */}
              <div className="bg-charcoal-900 text-white p-8 rounded-sm">
                <div className="text-center mb-8">
                  <div className="text-xs font-light tracking-[0.3em] mb-2 uppercase opacity-80">Charter Rate</div>
                  {(() => {
                    const priceInfo = formatCharterPrice(yacht.charter_rates);
                    return (
                      <>
                        <div className="text-4xl font-thin mb-2">{priceInfo.price}</div>
                        <div className="text-xs font-light opacity-60 uppercase tracking-wide">per week</div>
                      </>
                    );
                  })()}
                </div>
                
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-sm font-light">Charter Rate</span>
                    {(() => {
                      const priceInfo = formatCharterPrice(yacht.charter_rates);
                      return <span className="text-sm font-light">{priceInfo.price}</span>;
                    })()}
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-sm font-light">Fuel</span>
                    <span className="text-sm font-light opacity-70">{yacht.pricing?.extras?.fuel || 'Consumption'}</span>
                  </div>
                </div>
                
                <button 
                  onClick={() => onBookNow(yacht)}
                  className="w-full py-4 bg-white text-charcoal-900 text-sm font-light tracking-wide hover:bg-warm-gray-100 transition-all uppercase mb-4"
                >
                  Book Now
                </button>
                
                <button className="w-full py-3 border border-white/30 text-white text-sm font-light tracking-wide hover:bg-white/10 transition-all uppercase">
                  Request Brochure
                </button>
              </div>

              {/* Contact Card */}
              <div className="bg-warm-gray-50 p-6 rounded-sm">
                <h4 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Charter Specialist</h4>
                <div className="space-y-4">
                  <div>
                    <div className="text-lg font-light text-charcoal-900 mb-1">Christopher WARREN</div>
                    <div className="text-sm font-light text-charcoal-600">Yacht Charter Specialist</div>
                  </div>
                  <div className="space-y-2">
                    <a href="tel:+17868400745" className="flex items-center text-sm font-light text-charcoal-700 hover:text-charcoal-900 transition-colors">
                      <Phone className="w-4 h-4 mr-2" />
                      +1 786 840 0745
                    </a>
                    <a href="mailto:chris@priviacollection.com" className="flex items-center text-sm font-light text-charcoal-700 hover:text-charcoal-900 transition-colors">
                      <Mail className="w-4 h-4 mr-2" />
                      chris@priviacollection.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Availability - Hidden as we don't have real availability data */}
              {/*
              <div className="border border-warm-gray-200 p-6 rounded-sm">
                <h4 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Availability</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light text-charcoal-700">Juin 2024</span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light text-charcoal-700">Juillet 2024</span>
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light text-charcoal-700">Août 2024</span>
                    <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light text-charcoal-700">Septembre 2024</span>
                    <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs font-light text-charcoal-500 mt-4 pt-4 border-t border-warm-gray-200">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                    Disponible
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                    Réservé
                  </div>
                </div>
              </div>
              */}
            </div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-20 text-center bg-charcoal-900 text-white p-16 rounded-sm">
          <h2 className="text-xs font-light tracking-[0.3em] mb-4 uppercase opacity-80">Ready for Escape?</h2>
          <h3 className="text-4xl font-thin mb-6 tracking-tight">Create Your Unique Experience</h3>
          <p className="text-lg font-light opacity-80 mb-8 max-w-2xl mx-auto">
            Our specialized advisors accompany you in creating a tailor-made journey, 
            adapted to your most demanding desires.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => onBookNow(yacht)}
              className="px-8 py-4 bg-white text-charcoal-900 text-sm font-light tracking-wide hover:bg-warm-gray-100 transition-all uppercase"
            >
              Book This Yacht
            </button>
            <button className="px-8 py-4 border border-white/30 text-white text-sm font-light tracking-wide hover:bg-white/10 transition-all uppercase">
              Discover Other Yachts
            </button>
          </div>
        </div>
      </div>
      
      {/* Photo Gallery Modal */}
      <PhotoGalleryModal 
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        yacht={yacht}
        initialPhotoIndex={initialPhotoIndex}
      />
    </div>
  );
};

// Hero Section - Elegant and minimal like PRIVIA
const Hero = () => {
  const { t } = useLanguage();
  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1523496922380-91d5afba98a3')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/40"></div>
      </div>
      
      <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-6">
        <div className="mb-6">
          <h1 className="text-sm font-light tracking-[0.3em] mb-4 opacity-90">LUXURY YACHT CHARTER</h1>
          <h2 className="text-6xl lg:text-8xl font-thin leading-none mb-4 tracking-tight">
            {t.hero.title.split(' ')[0].toUpperCase()}
          </h2>
          <h3 className="text-xl lg:text-2xl font-light tracking-wide opacity-80">
            {t.hero.subtitle}
          </h3>
        </div>
        
        <p className="text-base lg:text-lg font-light leading-relaxed mb-12 opacity-75 max-w-2xl mx-auto">
          {t.hero.description}
        </p>
        
        <button className="inline-flex items-center px-8 py-3 border border-white/30 text-white text-sm font-light tracking-wide hover:bg-white hover:text-charcoal-900 transition-all duration-500 backdrop-blur-sm">
          {t.hero.cta.toUpperCase()}
          <ChevronDown className="w-4 h-4 ml-2" />
        </button>
      </div>
    </section>
  );
};

// Featured Yachts Section - Optimized to load only 6 featured yachts
const FeaturedYachtsSection = ({ onBookNow }) => {
  const [featuredYachts, setFeaturedYachts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedYachts = async () => {
      try {
        console.log('🌟 FEATURED YACHTS: Fetching featured yachts...');
        console.log('🔗 API URL:', `${API}/yachts/featured`);
        
        // Utiliser le cache pour éviter les requêtes répétées
        const data = await cachedFetch(`${API}/yachts/featured`);
        
        console.log('✅ FEATURED YACHTS: Raw data received:', data);
        console.log('🔍 Data type:', typeof data);
        console.log('📊 Is array?', Array.isArray(data));
        console.log('📏 Data length:', data?.length);
        
        // Vérifier que data est bien un tableau
        if (Array.isArray(data)) {
          console.log('✅ Data is array, setting featured yachts');
          setFeaturedYachts(data);
        } else {
          console.error('❌ Data is not an array:', data);
          setFeaturedYachts([]); // Fallback à un tableau vide
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ FEATURED YACHTS: Error fetching featured yachts:', error);
        // Fallback optimisé: charger seulement 6 yachts au lieu de tous
        console.log('🔄 FEATURED YACHTS: Trying fallback...');
        try {
          const data = await cachedFetch(`${API}/yachts?limit=6&offset=0`);
          console.log('✅ FEATURED YACHTS: Fallback data received:', data);
          console.log('🔍 Fallback data type:', typeof data);
          console.log('📊 Fallback has yachts?', data?.yachts);
          
          if (data?.yachts && Array.isArray(data.yachts)) {
            console.log('✅ Setting fallback yachts:', data.yachts.length);
            setFeaturedYachts(data.yachts);
          } else {
            console.error('❌ Fallback data invalid:', data);
            setFeaturedYachts([]);
          }
          setLoading(false);
        } catch (fallbackError) {
          console.error('❌ FEATURED YACHTS: Fallback also failed:', fallbackError);
          setFeaturedYachts([]);
          setLoading(false);
        }
      }
    };

    fetchFeaturedYachts();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border border-charcoal-300 border-t-charcoal-900 rounded-full animate-spin mx-auto mb-4"></div>
        <div className="text-sm font-light text-charcoal-600">Loading featured yachts...</div>
      </div>
    );
  }

  if (featuredYachts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-light text-charcoal-600">No featured yachts available at the moment.</p>
        <button 
          onClick={() => navigate('/collection')}
          className="mt-4 px-6 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all duration-300 uppercase"
        >
          View All Yachts
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
      {featuredYachts.map(yacht => (
        <YachtCard 
          key={yacht.id} 
          yacht={yacht} 
          onBookNow={onBookNow}
        />
      ))}
    </div>
  );
};

// Search Section - Refined and elegant with functional search (Optimized)
const SearchSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    region: '',
    destination: '',
    guests: '',
    budget: ''
  });

  // Destination data - HARDCODED for performance (no API call needed)
  const destinationData = {
    'Mediterranean': [
      'Amalfi Coast',
      'Corsica', 
      'Croatia',
      'Dubai',
      'France',
      'French Riviera',
      'Greece',
      'Italy',
      'Malta',
      'Monaco',
      'Montenegro',
      'Mykonos',
      'Sardinia',
      'St Tropez',
      'Turkey'
    ],
    'Caribbean': [
      'Antigua',
      'Bahamas',
      'Cuba',
      'Martinique',
      'Saint Martin',
      'St Barts',
      'Virgin Islands'
    ]
  };

  // Budget ranges per week (adjusted to real yacht prices)
  const budgetRanges = [
    { value: '', label: 'Budget per week' },
    { value: '300000-500000', label: '$300,000 - $500,000 USD' },
    { value: '500000-700000', label: '$500,000 - $700,000 USD' },
    { value: '700000-1000000', label: '$700,000 - $1,000,000 USD' },
    { value: '1000000-1500000', label: '$1,000,000 - $1,500,000 USD' },
    { value: '1500000-2000000', label: '$1,500,000 - $2,000,000 USD' },
    { value: '2000000+', label: '$2,000,000+ USD' }
  ];

  const handleRegionChange = (region) => {
    setSearchParams({
      ...searchParams,
      region,
      destination: '' // Reset destination when region changes
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build search query parameters
    const queryParams = new URLSearchParams();
    
    if (searchParams.region) queryParams.set('region', searchParams.region);
    if (searchParams.destination) {
      queryParams.set('destination', searchParams.destination);
    }
    if (searchParams.guests) queryParams.set('guests', searchParams.guests);
    if (searchParams.budget) queryParams.set('budget', searchParams.budget);
    
    // Navigate to collection page with search parameters
    navigate(`/collection?${queryParams.toString()}`);
  };

  return (
    <section className="py-20 bg-warm-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4">YOUR ESCAPE</h2>
          <h3 className="text-4xl font-thin text-charcoal-900 mb-6">Let Yourself Be Inspired</h3>
        </div>
        
        <div className="bg-white rounded-sm shadow-lg border border-warm-gray-100 p-8">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Region Selection */}
            <div>
              <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">
                Region
              </label>
              <select 
                className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light"
                value={searchParams.region}
                onChange={(e) => handleRegionChange(e.target.value)}
              >
                <option value="">Select region</option>
                <option value="Mediterranean">Mediterranean</option>
                <option value="Caribbean">Caribbean</option>
              </select>
            </div>

            {/* Destination Selection */}
            <div>
              <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">
                Destination
              </label>
              <select 
                className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light"
                value={searchParams.destination}
                onChange={(e) => setSearchParams({...searchParams, destination: e.target.value})}
                disabled={!searchParams.region}
              >
                {!searchParams.region ? (
                  <option value="">Select region first</option>
                ) : (
                  <>
                    <option value="">Select destination (optional)</option>
                    {destinationData[searchParams.region]?.map(destination => (
                  <option key={destination} value={destination}>{destination}</option>
                ))}
                  </>
                )}
              </select>
            </div>
            
            {/* Guests Selection */}
            <div>
              <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">
                Privileged Circle
              </label>
              <select 
                className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light"
                value={searchParams.guests}
                onChange={(e) => setSearchParams({...searchParams, guests: e.target.value})}
              >
                <option value="">Number of guests</option>
                <option value="6">Intimate circle 2-6</option>
                <option value="12">Private gathering 7-12</option>
                <option value="16">Celebration 13-16</option>
                <option value="20">Grand event 17+</option>
              </select>
            </div>
            
            {/* Budget Selection */}
            <div>
              <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">
                Budget
              </label>
              <select 
                className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light"
                value={searchParams.budget}
                onChange={(e) => setSearchParams({...searchParams, budget: e.target.value})}
              >
                {budgetRanges.map(range => (
                  <option key={range.value} value={range.value}>{range.label}</option>
                ))}
              </select>
            </div>
            
            {/* Submit Button */}
            <div className="flex items-end">
              <button 
                type="submit"
                className="w-full p-4 bg-charcoal-900 text-white text-xs font-light tracking-wide hover:bg-charcoal-800 transition-all duration-300 uppercase"
              >
                Create Your Escape
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// Yacht Card - Ultra refined like PRIVIA
const YachtCard = ({ yacht, onBookNow }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    // Use SEO-friendly URL if available, fallback to old format
    const url = yacht.seo_url || `/yacht/${yacht.id}`;
    navigate(url);
  };

  return (
    <div className="group cursor-pointer">
      <div className="relative mb-6 overflow-hidden" onClick={handleCardClick}>
        <LazyImage 
          src={yacht.image_url} 
          alt={yacht.name}
          className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1">
          <span className="text-xs font-light text-white tracking-wide">{yacht.type}</span>
        </div>
        <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onBookNow(yacht);
            }}
            className="px-6 py-2 border border-white/50 text-white text-xs font-light tracking-wide hover:bg-white hover:text-charcoal-900 transition-all duration-300 backdrop-blur-sm"
          >
            BOOK NOW
          </button>
        </div>
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-light text-charcoal-900 tracking-wide">{yacht.name}</h3>
          <div className="text-right">
            <p className="text-lg font-light text-charcoal-900">
              {formatWeeklyRate(yacht.weekly_rate)}
            </p>
            <p className="text-xs font-light text-charcoal-500 tracking-wide uppercase">per week</p>
          </div>
        </div>
        
        <p className="text-sm font-light text-charcoal-600 leading-relaxed line-clamp-2">
          {yacht.description}
        </p>
        
        <div className="flex justify-between items-center text-xs font-light text-charcoal-500 tracking-wide uppercase pt-2">
          <div className="flex items-center">
            <MapPin className="w-3 h-3 mr-1" />
            {yacht.location}
          </div>
          <div className="flex items-center">
            <Users className="w-3 h-3 mr-1" />
            {yacht.guests} guests
          </div>
        </div>
      </div>
    </div>
  );
};

// Booking Modal - Elegant and refined
const BookingModal = ({ yacht, isOpen, onClose, onSubmit }) => {
  const [bookingData, setBookingData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    start_date: '',
    end_date: '',
    guests: '',
    special_requests: '',
    // Honeypot field - invisible to users, only bots fill this
    company: '' // If filled, it's spam
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 🛡️ HONEYPOT VALIDATION - Block spam bots
      if (bookingData.company && bookingData.company.trim() !== '') {
        console.log('🚨 BOOKING SPAM DETECTED: Honeypot field filled:', bookingData.company);
        alert('❌ Spam detected. Your booking request has been blocked.');
        setIsSubmitting(false);
        return;
      }

      console.log('🛡️ Booking honeypot check: PASSED (company field empty)');

      // Prepare email data
      const emailData = {
        ...bookingData,
        yacht_name: yacht.name,
        yacht_id: yacht.id,
        total_price: yacht.price_per_day
      };

      console.log('\n🚀 ===== FRONTEND BOOKING SUBMISSION =====');
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('📋 Email data being sent:', JSON.stringify(emailData, null, 2));
      console.log('🌐 API Endpoint:', `${API}/send-booking-emails`);

      // Send emails via our email service
      const response = await fetch(`${API}/send-booking-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      console.log('📡 HTTP Response Status:', response.status, response.statusText);
      console.log('📋 Response Headers:', Object.fromEntries(response.headers.entries()));

      const result = await response.json();
      
      console.log('📨 API Response Body:', JSON.stringify(result, null, 2));

      if (result.success) {
        console.log('✅ ===== BOOKING SUCCESS =====');
        console.log('📧 Client Email ID:', result.details?.clientEmailId);
        console.log('📧 Internal Email ID:', result.details?.internalEmailId);
        console.log('⏱️ Processing Time:', result.details?.processingTime + 'ms');
        console.log('🎯 Success Message:', result.message);
        console.log('=======================================\n');
        
        alert(`✅ Booking request sent successfully!

Thank you ${emailData.client_name} for your interest in ${emailData.yacht_name}.

📧 A confirmation email has been sent to ${emailData.client_email}

Our team will contact you within 24 hours to finalize your booking and customize your experience.

🛥️ Privia Yachts Charter - Your luxury escape awaits!`);
        
        // Reset form
        setBookingData({
          client_name: '',
          client_email: '',
          client_phone: '',
          start_date: '',
          end_date: '',
          guests: '',
          special_requests: '',
          company: '' // Reset honeypot field
        });
        
        onClose();
      } else {
        console.error('❌ ===== BOOKING FAILED =====');
        console.error('💥 Error message:', result.message);
        console.error('🔍 Error details:', result.error);
        console.error('=====================================\n');
        
        throw new Error(result.message || 'Failed to send booking request');
      }
    } catch (error) {
      console.error('\n❌ ===== FRONTEND ERROR =====');
      console.error('💥 Error type:', error.constructor.name);
      console.error('📝 Error message:', error.message);
      console.error('📚 Error stack:', error.stack);
      console.error('===============================\n');
      
      alert(`❌ There was an error sending your booking request.

Error details: ${error.message}

Please try again or contact us directly:
📞 +1 786 840 0745
📧 chris@priviayachts.com`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        <div className="fixed inset-0 bg-charcoal-900/75 backdrop-blur-sm transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-middle bg-white text-left overflow-hidden shadow-2xl transform transition-all max-w-lg w-full">
          <div className="bg-white px-8 pt-8 pb-6">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h3 className="text-xs font-light tracking-[0.3em] text-charcoal-600 mb-2 uppercase">
                  Booking Request
                </h3>
                <h4 className="text-2xl font-light text-charcoal-900 tracking-wide">
                  {yacht.name}
                </h4>
              </div>
              <button onClick={onClose} className="text-charcoal-400 hover:text-charcoal-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <input 
                    type="text"
                    required
                    placeholder="Full Name"
                    className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light placeholder-charcoal-400"
                    value={bookingData.client_name}
                    onChange={(e) => setBookingData({...bookingData, client_name: e.target.value})}
                  />
                </div>
                
                <div>
                  <input 
                    type="email"
                    required
                    placeholder="Email Address"
                    className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light placeholder-charcoal-400"
                    value={bookingData.client_email}
                    onChange={(e) => setBookingData({...bookingData, client_email: e.target.value})}
                  />
                </div>
                
                <div>
                  <input 
                    type="tel"
                    required
                    placeholder="Phone Number"
                    className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light placeholder-charcoal-400"
                    value={bookingData.client_phone}
                    onChange={(e) => setBookingData({...bookingData, client_phone: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-2 uppercase">Start Date</label>
                    <input 
                      type="date"
                      required
                      className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light"
                      value={bookingData.start_date}
                      onChange={(e) => setBookingData({...bookingData, start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-2 uppercase">End Date</label>
                    <input 
                      type="date"
                      required
                      className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light"
                      value={bookingData.end_date}
                      onChange={(e) => setBookingData({...bookingData, end_date: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <input 
                    type="number"
                    required
                    max={yacht.guests}
                    placeholder="Number of Guests"
                    className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light placeholder-charcoal-400"
                    value={bookingData.guests}
                    onChange={(e) => setBookingData({...bookingData, guests: e.target.value})}
                  />
                </div>
                
                <div>
                  <textarea 
                    rows="4"
                    placeholder="Special requests or preferences..."
                    className="w-full p-4 border-0 border-b border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light placeholder-charcoal-400 resize-none"
                    value={bookingData.special_requests}
                    onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
                  />
                </div>

                {/* 🛡️ HONEYPOT FIELD - Invisible to users, only bots fill this */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                  <input 
                    type="text"
                    name="company"
                    placeholder="Company name (leave empty)"
                    tabIndex="-1"
                    autoComplete="off"
                    value={bookingData.company}
                    onChange={(e) => setBookingData({...bookingData, company: e.target.value})}
                    aria-hidden="true"
                  />
                </div>
              </div>
              
              <div className="flex space-x-4 pt-4">
                <button 
                  type="button"
                  onClick={onClose}
                  className="flex-1 p-4 border border-warm-gray-300 text-charcoal-600 text-xs font-light tracking-wide hover:bg-warm-gray-50 transition-all duration-300 uppercase"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 p-4 bg-charcoal-900 text-white text-xs font-light tracking-wide hover:bg-charcoal-800 transition-all duration-300 uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Booking Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Escapes Page Component - SEO Optimized
const EscapesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1617330666625-e75bee2e333b')`
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-sm font-light tracking-[0.3em] mb-4 opacity-90 uppercase">Private Escapes</h1>
          <h2 className="text-5xl lg:text-6xl font-thin leading-none mb-4 tracking-tight">
            EXCLUSIVE DESTINATIONS
          </h2>
          <p className="text-lg font-light leading-relaxed opacity-75 max-w-2xl mx-auto">
            Explore our yacht charter destinations in the Mediterranean and Caribbean. From Monaco to Saint-Tropez, 
            from Greek Islands to the Caribbean, discover luxury yacht rentals in the world's most exclusive locations.
          </p>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Our Destinations</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6">Exceptional Escapes</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mediterranean */}
            <div className="group">
              <div className="relative overflow-hidden rounded-sm h-64 mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1617330666625-e75bee2e333b"
                  alt="Mediterranean"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h4 className="text-xl font-light text-white tracking-wide mb-1">Mediterranean</h4>
                  <p className="text-sm text-white/80">Monaco • Saint-Tropez • Portofino</p>
                </div>
              </div>
              <h4 className="text-xl font-light text-charcoal-900 mb-2">Eternal Riviera</h4>
              <p className="text-sm font-light text-charcoal-600 leading-relaxed mb-4">
                From Monaco to Portofino, explore the jewels of the French Riviera and Italian coast. 
                Prestigious anchorages, private beaches and the Mediterranean art of living.
              </p>
              <div className="text-xs font-light text-charcoal-500 uppercase tracking-wide">
                Ideal Season: May - October
              </div>
            </div>

            {/* Caribbean */}
            <div className="group">
              <div className="relative overflow-hidden rounded-sm h-64 mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1719664026730-85137c88c7b9"
                  alt="Caribbean"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h4 className="text-xl font-light text-white tracking-wide mb-1">Caribbean</h4>
                  <p className="text-sm text-white/80">St. Barts • Antigua • St. Martin</p>
                </div>
              </div>
              <h4 className="text-xl font-light text-charcoal-900 mb-2">Tropical Paradise</h4>
              <p className="text-sm font-light text-charcoal-600 leading-relaxed mb-4">
                Winter escape in the crystal-clear waters of the West Indies. White sand beaches, 
                coral reefs and authentic Creole hospitality.
              </p>
              <div className="text-xs font-light text-charcoal-500 uppercase tracking-wide">
                Ideal Season: December - April
              </div>
            </div>

            {/* Greek Islands */}
            <div className="group">
              <div className="relative overflow-hidden rounded-sm h-64 mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1664268406930-fa8a02c6e09f"
                  alt="Greek Islands"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h4 className="text-xl font-light text-white tracking-wide mb-1">Greek Islands</h4>
                  <p className="text-sm text-white/80">Mykonos • Santorini • Paros</p>
                </div>
              </div>
              <h4 className="text-xl font-light text-charcoal-900 mb-2">Mythical Archipelago</h4>
              <p className="text-sm font-light text-charcoal-600 leading-relaxed mb-4">
                Navigate through the ancient history of the Cyclades. Immaculate white architecture, 
                legendary sunsets and millennial maritime tradition.
              </p>
              <div className="text-xs font-light text-charcoal-500 uppercase tracking-wide">
                Ideal Season: April - October
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exclusive Experiences */}
      <section className="py-24 bg-warm-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Exclusive Experiences</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6">Beyond the Journey</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <h4 className="text-2xl font-light text-charcoal-900 mb-4">Secret Anchorages</h4>
              <p className="text-base font-light text-charcoal-600 leading-relaxed mb-6">
                Access the most preserved coves, private beaches and confidential anchorages 
                known only to our expert captains.
              </p>
              <ul className="space-y-2 text-sm font-light text-charcoal-600">
                <li>• Wild coves inaccessible by land</li>
                <li>• Historic anchorages of great families</li>
                <li>• Privileged access to marine reserves</li>
              </ul>
            </div>

            <div>
              <h4 className="text-2xl font-light text-charcoal-900 mb-4">Maritime Concierge</h4>
              <p className="text-base font-light text-charcoal-600 leading-relaxed mb-6">
                Our dedicated team organizes every detail of your escape: 
                exclusive reservations, bespoke activities and privileged experiences.
              </p>
              <ul className="space-y-2 text-sm font-light text-charcoal-600">
                <li>• Reservations at Michelin-starred restaurants</li>
                <li>• Access to private events and galas</li>
                <li>• Personalized nautical and land activities</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Fonction pour formater le prix hebdomadaire à partir des données de l'API
const formatWeeklyRate = (weeklyRate) => {
  // Si c'est déjà une chaîne formatée (nouveau format API)
  if (typeof weeklyRate === 'string' && weeklyRate !== '') {
    return weeklyRate;
  }
  
  // Si c'est un objet avec amount (ancien format)
  if (weeklyRate && weeklyRate.amount) {
    return weeklyRate.amount;
  }
  
  return 'On Request';
};

// Fonction pour formater les prix de charter depuis les charter_rates (USD priorité, EUR fallback, par semaine)
const formatCharterPrice = (charterRates) => {
  if (!charterRates || charterRates.length === 0) return { price: 'On Request', currency: 'USD' };
  
  // Chercher d'abord un prix USD
  const usdRate = charterRates.find(rate => rate.rate_usd);
  if (usdRate && usdRate.rate_usd) {
    return {
      price: usdRate.rate_usd, // Déjà formaté: "$1,290,500"
      currency: 'USD',
      raw: usdRate.rate_usd
    };
  }
  
  // Sinon chercher un prix EUR
  const eurRate = charterRates.find(rate => rate.rate_eur);
  if (eurRate && eurRate.rate_eur) {
    return {
      price: eurRate.rate_eur, // Déjà formaté: "€1,100,000"
      currency: 'EUR',
      raw: eurRate.rate_eur
    };
  }
  
  return { price: 'On Request', currency: 'USD' };
};

// Fonction pour extraire le prix numérique pour le tri
const extractNumericPrice = (yacht) => {
  if (!yacht.weekly_rate || !yacht.weekly_rate.amount) return 0;
  
  // Extraire le nombre de la chaîne formatée (ex: "$1,290,500" → 1290500)
  const numericString = yacht.weekly_rate.amount.replace(/[€$,]/g, '');
  const price = parseInt(numericString);
  
  // Convertir EUR en USD pour comparaison uniforme (approximation)
  if (yacht.weekly_rate.currency === 'EUR') {
    return price * 1.1; // 1 EUR = 1.1 USD approximativement
  }
  
  return price || 0;
};

// Collection Page Component - SEO Optimized with Infinite Scroll
const CollectionPage = () => {
  const navigate = useNavigate();
  const [yachts, setYachts] = useState([]);
  const [filteredYachts, setFilteredYachts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalYachts, setTotalYachts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  
  // Filtres appliqués (utilisés pour l'API)
  const [searchParams, setSearchParams] = useState({
    region: '',
    destination: '',
    guests: '',
    budget: '',
    yearFrom: '',
    yearTo: ''
  });
  
  // Filtres temporaires (avant clic sur Search)
  const [tempFilters, setTempFilters] = useState({
    region: '',
    destination: '',
    guests: '',
    budget: '',
    yearFrom: '',
    yearTo: ''
  });
  
  // État pour le tri par prix
  const [sortBy, setSortBy] = useState(''); // '', 'price_asc', 'price_desc'
  
  const YACHTS_PER_PAGE = 60;
  
  // Années de construction (range typique pour les yachts de luxe)
  const MIN_YEAR = 1980;
  const MAX_YEAR = new Date().getFullYear();
  const DEFAULT_YEAR_FROM = 2000;
  const DEFAULT_YEAR_TO = MAX_YEAR;
  
  // Destination data - same as SearchSection for consistency
  const destinationData = {
    'Mediterranean': [
      'Amalfi Coast',
      'Corsica', 
      'Croatia',
      'Dubai',
      'France',
      'French Riviera',
      'Greece',
      'Italy',
      'Malta',
      'Monaco',
      'Montenegro',
      'Mykonos',
      'Sardinia',
      'St Tropez',
      'Turkey'
    ],
    'Caribbean': [
      'Antigua',
      'Bahamas',
      'Cuba',
      'Martinique',
      'Saint Martin',
      'St Barts',
      'Virgin Islands'
    ]
  };

  // Get URL search parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      region: urlParams.get('region') || '',
      destination: urlParams.get('destination') || '',
      guests: urlParams.get('guests') || '',
      budget: urlParams.get('budget') || '',
      yearFrom: urlParams.get('yearFrom') || '',
      yearTo: urlParams.get('yearTo') || ''
    };
    setSearchParams(params);
    setTempFilters(params); // Synchroniser les filtres temporaires avec les paramètres URL
  }, []);

  // Fonction pour charger les yachts (première page ou reset)
  const fetchYachts = async (page = 1, append = false, customSearchParams = null) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const offset = (page - 1) * YACHTS_PER_PAGE;
      console.log(`🚀 COLLECTION PAGE: Fetching page ${page} (offset: ${offset}, append: ${append})...`);
      
      // Construire l'URL avec les paramètres de recherche et pagination
      let apiUrl = `${API}/yachts?limit=${YACHTS_PER_PAGE}&offset=${offset}`;
      
      // Utiliser les paramètres fournis ou les paramètres actuels
      const currentSearchParams = customSearchParams || searchParams;
      
      // Ajouter les filtres de recherche si présents
      if (currentSearchParams.region) {
        // Utiliser la destination si spécifiée, sinon la région
        const locationParam = currentSearchParams.destination 
          ? currentSearchParams.destination 
          : currentSearchParams.region;
        apiUrl += `&location=${encodeURIComponent(locationParam)}`;
        console.log(`🔍 FRONTEND: Sending location filter = ${locationParam}`);
      }
      if (currentSearchParams.guests) {
        apiUrl += `&min_guests=${currentSearchParams.guests}`;
        console.log(`🔍 FRONTEND: Sending guests filter = ${currentSearchParams.guests}`);
      }
      if (currentSearchParams.budget) {
        apiUrl += `&budget=${currentSearchParams.budget}`;
        console.log(`🔍 FRONTEND: Sending budget filter = ${currentSearchParams.budget}`);
      }
      if (currentSearchParams.yearFrom) {
        apiUrl += `&yearFrom=${currentSearchParams.yearFrom}`;
        console.log(`🔍 FRONTEND: Sending yearFrom filter = ${currentSearchParams.yearFrom}`);
      }
      if (currentSearchParams.yearTo) {
        apiUrl += `&yearTo=${currentSearchParams.yearTo}`;
        console.log(`🔍 FRONTEND: Sending yearTo filter = ${currentSearchParams.yearTo}`);
      }
      
      const response = await cachedFetch(apiUrl);
      console.log('✅ COLLECTION PAGE: Response received:', response);
      
      // Gérer les deux formats de réponse (ancien et nouveau)
      let yachtsData, paginationData;
      if (response.yachts && response.total !== undefined) {
        // Nouveau format avec yachts + total + limit + offset
        yachtsData = response.yachts;
        paginationData = {
          currentPage: page,
          totalPages: Math.ceil(response.total / YACHTS_PER_PAGE),
          totalCount: response.total,
          hasNextPage: (response.offset + response.limit) < response.total,
          hasPrevPage: page > 1
        };
        console.log('📊 COLLECTION PAGE: Using new API format');
      } else if (response.yachts && response.pagination) {
        // Format avec pagination explicite
        yachtsData = response.yachts;
        paginationData = response.pagination;
        console.log('📊 COLLECTION PAGE: Using pagination format');
      } else {
        // Ancien format (array direct)
        yachtsData = Array.isArray(response) ? response : [];
        paginationData = {
          currentPage: page,
          totalPages: yachtsData.length === YACHTS_PER_PAGE ? page + 1 : page,
          totalCount: (page - 1) * YACHTS_PER_PAGE + yachtsData.length,
          hasNextPage: yachtsData.length === YACHTS_PER_PAGE,
          hasPrevPage: page > 1
        };
        console.log('📊 COLLECTION PAGE: Using legacy format');
      }
      
      console.log('📊 COLLECTION PAGE: Yachts received:', yachtsData.length);
      console.log('📊 COLLECTION PAGE: Pagination:', paginationData);
      
      if (append) {
        // Ajouter les nouveaux yachts à la liste existante
        setYachts(prevYachts => [...prevYachts, ...yachtsData]);
        setFilteredYachts(prevYachts => [...prevYachts, ...yachtsData]);
      } else {
        // Remplacer la liste (nouveau search ou première page)
        setYachts(yachtsData);
        setFilteredYachts(yachtsData);
      }
      
      setTotalPages(paginationData.totalPages);
      setTotalYachts(paginationData.totalCount);
      setHasMorePages(paginationData.hasNextPage);
      
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
        console.error('❌ COLLECTION PAGE: Error fetching yachts:', error);
        // PRODUCTION: Pas de données de fallback - laisser l'état de chargement gérer l'erreur
      setLoading(false);
      setLoadingMore(false);
    }
  }; // Fonction normale, pas de useCallback
  
  // Charger les yachts au montage du composant
  useEffect(() => {
    fetchYachts(1, false); // Charger la première page
  }, []); // Pas de dépendances - se déclenche seulement au montage

  // Recharger quand searchParams change (déclenché par le bouton Search)
  const searchParamsRef = useRef(searchParams);
  useEffect(() => {
    // Seulement si les paramètres ont vraiment changé
    if (JSON.stringify(searchParamsRef.current) !== JSON.stringify(searchParams)) {
      searchParamsRef.current = searchParams;
      if (Object.values(searchParams).some(param => param)) {
        fetchYachts(1, false, searchParams); // Nouvelle recherche avec les nouveaux paramètres
      }
    }
  }, [searchParams]);

  // Fonction pour charger la page suivante (infinite scroll)
  const loadMoreYachts = useCallback(async () => {
    if (!loadingMore && hasMorePages && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      await fetchYachts(nextPage, true); // Append = true pour ajouter à la liste
    }
  }, [currentPage, loadingMore, hasMorePages, loading]);

  // Hook pour détecter le scroll vers le bas
  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Déclencher le chargement quand on arrive à 200px du bas
      if (scrollTop + windowHeight >= documentHeight - 200) {
        loadMoreYachts();
      }
    }, 200); // Throttle à 200ms pour éviter trop d'appels

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreYachts]);

  // Fonction pour trier les yachts par prix
  const sortYachts = (yachtsToSort) => {
    if (!sortBy) return yachtsToSort;
    
    const sorted = [...yachtsToSort].sort((a, b) => {
      const priceA = extractNumericPrice(a);
      const priceB = extractNumericPrice(b);
      
      if (sortBy === 'price_asc') {
        return priceA - priceB;
      } else if (sortBy === 'price_desc') {
        return priceB - priceA;
      }
      
      return 0;
    });
    
    return sorted;
  };

  // Les yachts à afficher avec tri appliqué (déjà filtrés et paginés par l'API)
  const displayYachts = sortYachts(yachts);

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1569263979104-865ab7cd8d13')`
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <h1 className="text-sm font-light tracking-[0.3em] mb-4 opacity-90 uppercase">Collection Exclusive</h1>
          <h2 className="text-5xl lg:text-6xl font-thin leading-none mb-4 tracking-tight">
            YACHTS D'EXCEPTION
          </h2>
          <p className="text-lg font-light leading-relaxed opacity-75 max-w-2xl mx-auto">
            Découvrez notre flotte rigoureusement sélectionnée de super yachts, symboles d'excellence maritime et de raffinement absolu.
          </p>
        </div>
      </section>

      {/* Fleet Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Notre Flotte</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6">Excellence Maritime</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-charcoal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-thin text-charcoal-900">25+</span>
              </div>
              <h4 className="text-lg font-light text-charcoal-900 mb-2">Motor Yachts</h4>
              <p className="text-sm font-light text-charcoal-600">De 30m à 150m</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-charcoal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-thin text-charcoal-900">12+</span>
              </div>
              <h4 className="text-lg font-light text-charcoal-900 mb-2">Sailing Yachts</h4>
              <p className="text-sm font-light text-charcoal-600">Tradition & Performance</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-charcoal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-thin text-charcoal-900">8+</span>
              </div>
              <h4 className="text-lg font-light text-charcoal-900 mb-2">Explorer Yachts</h4>
              <p className="text-sm font-light text-charcoal-600">Aventures Polaires</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-charcoal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-thin text-charcoal-900">24/7</span>
              </div>
              <h4 className="text-lg font-light text-charcoal-900 mb-2">Service Dédié</h4>
              <p className="text-sm font-light text-charcoal-600">Expertise Mondiale</p>
            </div>
          </div>

          {/* Elegant Filter Section */}
          <div className="mb-12 bg-white border border-warm-gray-200 rounded-sm shadow-sm">
            <div className="p-6">
              <h3 className="text-lg font-light text-charcoal-900 mb-6 tracking-wide">Refine Your Search</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Region Filter */}
                <div>
                  <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">Region</label>
                  <select 
                    value={tempFilters.region} 
                    onChange={(e) => setTempFilters({...tempFilters, region: e.target.value, destination: ''})}
                    className="w-full p-3 border border-warm-gray-200 bg-white text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light text-sm"
                  >
                    <option value="">All Regions</option>
                    <option value="Mediterranean">Mediterranean</option>
                    <option value="Caribbean">Caribbean</option>
                  </select>
                </div>
                
                {/* Destination Filter */}
                <div>
                  <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">Destination</label>
                  <select 
                    value={tempFilters.destination} 
                    onChange={(e) => setTempFilters({...tempFilters, destination: e.target.value})}
                    disabled={!tempFilters.region}
                    className="w-full p-3 border border-warm-gray-200 bg-white text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light text-sm disabled:opacity-50"
                  >
                    {!tempFilters.region ? (
                      <option value="">Select region first</option>
                    ) : (
                      <>
                        <option value="">Select destination (optional)</option>
                        {destinationData[tempFilters.region]?.map(destination => (
                      <option key={destination} value={destination}>{destination}</option>
                    ))}
                      </>
                    )}
                  </select>
                </div>
                
                {/* Guests Filter */}
                <div>
                  <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">Guests</label>
                  <select 
                    value={tempFilters.guests} 
                    onChange={(e) => setTempFilters({...tempFilters, guests: e.target.value})}
                    className="w-full p-3 border border-warm-gray-200 bg-white text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light text-sm"
                  >
                    <option value="">Any number</option>
                    <option value="6">Up to 6 guests</option>
                    <option value="12">Up to 12 guests</option>
                    <option value="16">Up to 16 guests</option>
                    <option value="20">16+ guests</option>
                  </select>
                </div>
                
                {/* Budget Filter */}
                <div>
                  <label className="block text-xs font-light tracking-wide text-charcoal-600 mb-3 uppercase">Weekly Budget</label>
                  <select 
                    value={tempFilters.budget} 
                    onChange={(e) => setTempFilters({...tempFilters, budget: e.target.value})}
                    className="w-full p-3 border border-warm-gray-200 bg-white text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light text-sm"
                  >
                    <option value="">Any budget</option>
                    <option value="300000-500000">$300K - $500K</option>
                    <option value="500000-700000">$500K - $700K</option>
                    <option value="700000-1000000">$700K - $1M</option>
                    <option value="1000000-1500000">$1M - $1.5M</option>
                    <option value="1500000-2000000">$1.5M - $2M</option>
                    <option value="2000000+">$2M+</option>
                  </select>
                </div>
              </div>
              
              {/* Search and Clear Buttons */}
              <div className="flex justify-center items-center space-x-4 mt-6">
                {/* Search Button */}
                  <button
                    onClick={() => {
                    const newFilters = {...tempFilters};
                    setSearchParams(newFilters);
                    // Pas besoin de re-synchroniser tempFilters car ils sont déjà à jour
                      setCurrentPage(1);
                    setYachts([]);
                    setFilteredYachts([]);
                    setHasMorePages(true);
                  }}
                  className="px-8 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all duration-300 uppercase"
                >
                  Search
                </button>
                
                {/* Clear All Filters Button */}
                {(Object.values(searchParams).some(param => param) || Object.values(tempFilters).some(param => param)) && (
                  <button
                    onClick={() => {
                      setTempFilters({ region: '', destination: '', guests: '', budget: '', yearFrom: '', yearTo: '' });
                      setSearchParams({ region: '', destination: '', guests: '', budget: '', yearFrom: '', yearTo: '' });
                      setCurrentPage(1);
                      setYachts([]);
                      setFilteredYachts([]);
                      setHasMorePages(true);
                    }}
                    className="px-6 py-2 border border-charcoal-300 text-charcoal-700 text-sm font-light tracking-wide hover:bg-charcoal-50 transition-all duration-300 uppercase"
                  >
                    Clear All Filters
                  </button>
              )}
              </div>
            </div>
          </div>

          {/* Sort by Price and Type Filter */}
          <div className="mb-8 flex justify-center items-center space-x-6">
            {/* Sort by Price Buttons */}
            <div className="inline-flex bg-warm-gray-100 rounded-sm p-1">
              <button
                onClick={() => setSortBy('')}
                className={`px-4 py-2 text-sm font-light tracking-wide transition-all duration-300 uppercase ${
                  sortBy === '' 
                    ? 'bg-white text-charcoal-900 shadow-sm' 
                    : 'text-charcoal-600 hover:text-charcoal-900'
                }`}
              >
                Default
              </button>
              <button
                onClick={() => setSortBy('price_asc')}
                className={`px-4 py-2 text-sm font-light tracking-wide transition-all duration-300 uppercase ${
                  sortBy === 'price_asc' 
                    ? 'bg-white text-charcoal-900 shadow-sm' 
                    : 'text-charcoal-600 hover:text-charcoal-900'
                }`}
              >
                Price ↑
              </button>
              <button
                onClick={() => setSortBy('price_desc')}
                className={`px-4 py-2 text-sm font-light tracking-wide transition-all duration-300 uppercase ${
                  sortBy === 'price_desc' 
                    ? 'bg-white text-charcoal-900 shadow-sm' 
                    : 'text-charcoal-600 hover:text-charcoal-900'
                }`}
              >
                Price ↓
              </button>
            </div>

            {/* Year Range Filter */}
            <div className="flex items-center space-x-4">
              <label className="text-sm font-light text-charcoal-600 uppercase tracking-wide">Built:</label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min={MIN_YEAR}
                  max={MAX_YEAR}
                  value={searchParams.yearFrom || DEFAULT_YEAR_FROM}
                  onChange={(e) => {
                    const newSearchParams = {...searchParams, yearFrom: e.target.value};
                    setSearchParams(newSearchParams);
                    setTempFilters(newSearchParams);
                  }}
                  className="w-20 px-2 py-1 border border-warm-gray-200 bg-white text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light text-sm rounded-sm text-center"
                  placeholder="From"
                />
                <span className="text-charcoal-400">-</span>
                <input
                  type="number"
                  min={MIN_YEAR}
                  max={MAX_YEAR}
                  value={searchParams.yearTo || DEFAULT_YEAR_TO}
                  onChange={(e) => {
                    const newSearchParams = {...searchParams, yearTo: e.target.value};
                    setSearchParams(newSearchParams);
                    setTempFilters(newSearchParams);
                  }}
                  className="w-20 px-2 py-1 border border-warm-gray-200 bg-white text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-colors font-light text-sm rounded-sm text-center"
                  placeholder="To"
                />
              </div>
            </div>
          </div>

          {/* Search Results Info */}
          {Object.values(searchParams).some(param => param) && (
            <div className="mb-8 p-4 bg-warm-gray-50 rounded-sm">
              <div className="text-sm font-light text-charcoal-600">
                {displayYachts.length > 0 ? (
                  <>Showing {displayYachts.length} yacht{displayYachts.length !== 1 ? 's' : ''} matching your criteria{totalYachts > displayYachts.length && ` (${totalYachts}+ total)`}</>
                ) : (
                  <>No yachts found matching your criteria. Try adjusting your filters.</>
                )}
              </div>
              {searchParams.region && (
                <div className="text-xs font-light text-charcoal-500 mt-1">
                  Region: {searchParams.region}
                  {searchParams.destination && (
                    <> • Destination: {searchParams.destination}</>
                  )}
                  {searchParams.guests && <> • Guests: {searchParams.guests}+</>}
                  {searchParams.budget && <> • Budget: ${searchParams.budget.replace('-', ' - ')} USD/week</>}
                </div>
              )}
            </div>
          )}
          
          {!loading && (
            <div className="mb-8 p-4 bg-warm-gray-50 rounded-sm">
              <div className="text-sm font-light text-charcoal-600">
                Showing {displayYachts.length} yachts
                {totalYachts > 0 && ` out of ${totalYachts}+ available`}
                {hasMorePages && " • Scroll down to see more"}
              </div>
            </div>
          )}

          {/* Yacht Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border border-charcoal-300 border-t-charcoal-900 rounded-full animate-spin mx-auto mb-4"></div>
              <div className="text-sm font-light text-charcoal-600">Loading collection...</div>
            </div>
          ) : displayYachts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {displayYachts.map(yacht => (
                <div key={yacht.id} className="group">
                  <div 
                    className="relative overflow-hidden rounded-sm h-64 mb-6 cursor-pointer"
                    onClick={() => navigate(yacht.seo_url || `/yacht/${yacht.id}`)}
                  >
                    <LazyImage 
                      src={yacht.image_url}
                      alt={yacht.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1 rounded">
                      <span className="text-xs font-light text-white tracking-wide">{yacht.type}</span>
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <h4 className="text-xl font-light text-white tracking-wide mb-1">{yacht.name}</h4>
                      <p className="text-sm text-white/80">{yacht.location}</p>
                    </div>
                    
                    {/* Hover overlay with view details */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 backdrop-blur-sm px-6 py-3 rounded-sm">
                        <span className="text-charcoal-900 text-sm font-light tracking-wide uppercase">View Details</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="text-lg font-light text-charcoal-900">{yacht.name}</h4>
                      <p className="text-sm font-light text-charcoal-600">{yacht.specifications?.length || yacht.length_display || 'N/A'} • {yacht.guests} guests</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-light text-charcoal-900">{formatWeeklyRate(yacht.weekly_rate)}</p>
                      <p className="text-xs font-light text-charcoal-500">per week</p>
                    </div>
                  </div>
                  
                  <p className="text-sm font-light text-charcoal-600 leading-relaxed mb-4 line-clamp-2">
                    {yacht.description}
                  </p>
                  
                  <button className="w-full py-3 border border-charcoal-300 text-charcoal-700 text-sm font-light tracking-wide hover:bg-charcoal-50 transition-all duration-300 uppercase">
                    View Details
                  </button>
                </div>
                ))}
              </div>
              
              {/* Infinite Scroll Loading Indicator */}
              {loadingMore && (
                <div className="flex justify-center items-center mt-16 py-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 border border-charcoal-300 border-t-charcoal-900 rounded-full animate-spin"></div>
                    <div className="text-sm font-light text-charcoal-600">
                      Loading more yachts...
                    </div>
                  </div>
                </div>
              )}
              
              {/* Manual Load More Button */}
              {hasMorePages && !loading && !loadingMore && displayYachts.length > 0 && (
                <div className="text-center mt-12 py-8">
                  <div className="mb-4">
                    <div className="text-sm font-light text-charcoal-600 mb-6">
                      {displayYachts.length} yacht{displayYachts.length !== 1 ? 's' : ''} displayed out of {totalYachts}
                    </div>
                  <button
                      onClick={loadMoreYachts}
                      className="group relative px-8 py-4 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all duration-300 uppercase overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      <div className="relative flex items-center space-x-2">
                        <span>Load More Yachts</span>
                        <svg className="w-4 h-4 transition-transform group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                  </button>
                  </div>
                  
                  <div className="text-xs font-light text-charcoal-500 mt-4">
                    💡 Tip: Automatic loading also triggers when scrolling down
                  </div>
                </div>
              )}
              
              {/* End of Results Indicator */}
              {!hasMorePages && !loading && displayYachts.length > 0 && (
                <div className="text-center mt-16 py-8">
                  <div className="inline-flex items-center space-x-2 px-6 py-3 bg-warm-gray-100 rounded-sm">
                    <div className="w-2 h-2 bg-charcoal-400 rounded-full"></div>
                    <div className="text-sm font-light text-charcoal-600">
                      You have seen all available yachts ({displayYachts.length} total)
                    </div>
                    <div className="w-2 h-2 bg-charcoal-400 rounded-full"></div>
                  </div>
                  
                  {/* Back to top button */}
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="mt-6 px-6 py-3 border border-charcoal-300 text-charcoal-700 text-sm font-light tracking-wide hover:bg-charcoal-50 transition-all duration-300 uppercase"
                  >
                    Back to Top
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-6 bg-charcoal-100 rounded-full flex items-center justify-center">
                <span className="text-charcoal-600 text-2xl">⚠️</span>
              </div>
              <div className="text-lg font-light text-charcoal-900 mb-4">Service temporairement indisponible</div>
              <div className="text-sm font-light text-charcoal-600 mb-6">
                Nous rencontrons actuellement des difficultés techniques. Veuillez réessayer dans quelques instants.
              </div>
              <button 
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all"
              >
                Actualiser la page
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Experience Page Component - Ultra Premium & Cinematic
const ExperiencePage = () => {
  const [currentSection, setCurrentSection] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Section - Cinematic Full Screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-110 transition-transform duration-[20s] ease-out"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1612377002236-fa07c613c6a7')`,
            animation: 'slowZoom 20s ease-out infinite alternate'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        </div>
        
        {/* Floating Navigation Dots */}
        <div className="absolute right-12 top-1/2 transform -translate-y-1/2 z-20">
          <div className="flex flex-col space-y-4">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className={`w-2 h-2 rounded-full transition-all duration-500 ${currentSection === index ? 'bg-white scale-125' : 'bg-white/30'}`}></div>
            ))}
          </div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-7xl mx-auto px-6">
          {/* Ornamental Top */}
          <div className="mb-12">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent mx-auto mb-6"></div>
            <div className="text-xs font-light tracking-[0.5em] mb-8 opacity-70 uppercase">
              Exceptional Experiences
            </div>
          </div>
          
          <div className="space-y-8">
            <h1 className="text-8xl lg:text-[12rem] font-thin leading-none tracking-tighter">
              <span className="block opacity-90">THE ART</span>
              <span className="block text-6xl lg:text-8xl opacity-60 -mt-4">of</span>
              <span className="block -mt-2">LIVING</span>
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <div className="text-xl lg:text-2xl font-light leading-relaxed opacity-80 mb-8">
                Transcend the boundaries of possibility in a universe where every moment 
                becomes a work of art, where excellence becomes your second nature.
              </div>
              
              {/* Luxury Stats Bar */}
              <div className="flex justify-center items-center space-x-16 mb-12">
                <div className="text-center group cursor-pointer">
                  <div className="text-4xl font-thin mb-2 transition-all duration-300 group-hover:scale-110">∞</div>
                  <div className="text-xs font-light tracking-wide uppercase opacity-60">Emotions</div>
                </div>
                
                <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent"></div>
                
                <div className="text-center group cursor-pointer">
                  <div className="text-4xl font-thin mb-2 transition-all duration-300 group-hover:scale-110">24</div>
                  <div className="text-xs font-light tracking-wide uppercase opacity-60">Hours</div>
                </div>
                
                <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent"></div>
                
                <div className="text-center group cursor-pointer">
                  <div className="text-4xl font-thin mb-2 transition-all duration-300 group-hover:scale-110">★</div>
                  <div className="text-xs font-light tracking-wide uppercase opacity-60">Excellence</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Ornamental Bottom */}
          <div className="mt-16">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent mx-auto mb-8"></div>
            <div className="text-xs font-light tracking-[0.3em] opacity-50 uppercase">
              Scroll to discover
            </div>
          </div>
        </div>
        
        {/* Animated Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-12 border border-white/20 rounded-full relative overflow-hidden">
            <div className="w-1 h-3 bg-white/60 rounded-full mx-auto mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Opening Statement - Luxury */}
      <section className="py-32 bg-gradient-to-b from-warm-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-96 h-96 border border-warm-gray-200/50 rounded-full"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 border border-warm-gray-200/30 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2px h-32 bg-gradient-to-b from-transparent via-warm-gray-300 to-transparent"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 text-center relative">
          <div className="mb-12">
            <div className="inline-block px-6 py-2 border border-warm-gray-300 rounded-full mb-8">
              <span className="text-xs font-light tracking-[0.3em] text-charcoal-600 uppercase">Philosophy</span>
            </div>
            <h2 className="text-6xl lg:text-7xl font-thin text-charcoal-900 mb-8 tracking-tight leading-none">
              REDEFINING<br />
              <span className="text-4xl lg:text-5xl opacity-60">EXCELLENCE</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-start">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-charcoal-100 to-charcoal-200 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-charcoal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </div>
              <h3 className="text-xl font-light text-charcoal-900 mb-4">Theatricality</h3>
              <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                Every moment orchestrated like a private performance where you are the star.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-charcoal-100 to-charcoal-200 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-charcoal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                </svg>
              </div>
              <h3 className="text-xl font-light text-charcoal-900 mb-4">Craftsmanship</h3>
              <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                Our artisans transform every service into a personalized masterpiece.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-charcoal-100 to-charcoal-200 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-charcoal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="text-xl font-light text-charcoal-900 mb-4">Transcendence</h3>
              <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                Exceed your boldest expectations in a setting of absolute exception.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter I - Gastronomie Haute Couture */}
      <section className="py-40 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Chapter Header */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="text-6xl font-thin text-charcoal-200 mr-8">01</div>
              <div>
                <div className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-2 uppercase">Chapter One</div>
                <h2 className="text-5xl font-thin text-charcoal-900 tracking-tight">Haute Couture Gastronomy</h2>
              </div>
            </div>
            <div className="w-full h-px bg-gradient-to-r from-charcoal-300 via-charcoal-100 to-transparent"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
            <div className="lg:col-span-7 relative">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-warm-gray-100 to-transparent rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <img 
                  src="https://images.unsplash.com/photo-1674606878551-f424ad6ce965"
                  alt="Gastronomie haute couture"
                  className="w-full h-[600px] object-cover rounded-sm relative z-10 transition-transform duration-700 group-hover:scale-[1.02]"
                />
                
                {/* Floating Credentials */}
                <div className="absolute -top-8 -right-8 bg-white p-8 rounded-sm shadow-2xl border border-warm-gray-100 z-20">
                  <div className="text-center">
                    <div className="text-3xl font-thin text-charcoal-900 mb-2">★★</div>
                    <div className="text-xs font-light text-charcoal-600 uppercase tracking-wide">Michelin</div>
                  </div>
                </div>
                
                <div className="absolute -bottom-8 -left-8 bg-charcoal-900 text-white p-8 rounded-sm z-20">
                  <div className="text-center">
                    <div className="text-2xl font-thin mb-2">Laurent</div>
                    <div className="text-xs font-light opacity-70 uppercase tracking-wide">Starred Chef</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-5 space-y-10">
              <div>
                <h3 className="text-4xl font-thin text-charcoal-900 mb-6 leading-tight">
                  Gustatory<br />
                  <span className="text-3xl opacity-60">Symphony</span>
                </h3>
                <p className="text-lg font-light text-charcoal-700 leading-relaxed mb-8">
                  Our starred chefs compose unique culinary scores for you, 
                  where each gustatory note resonates in perfect harmony with your most refined desires.
                </p>
              </div>
              
              {/* Luxury Process */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-charcoal-900 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-light">1</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-light text-charcoal-900 mb-2">Private Consultation</h4>
                    <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                      Personalized interview with our chef to understand your preferences and create your signature menu.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-charcoal-900 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-light">2</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-light text-charcoal-900 mb-2">Exceptional Sourcing</h4>
                    <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                      Selection of the rarest products from our exclusive suppliers in each port of call.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-charcoal-900 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-light">3</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-light text-charcoal-900 mb-2">Theatrical Staging</h4>
                    <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                      Theatrical presentation of each dish in a personalized setting according to the desired ambiance.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Premium Stats */}
              <div className="grid grid-cols-2 gap-6 pt-8">
                <div className="text-center p-6 bg-gradient-to-br from-warm-gray-50 to-warm-gray-100 rounded-sm">
                  <div className="text-3xl font-thin text-charcoal-900 mb-2">5★</div>
                  <div className="text-xs font-light text-charcoal-600 uppercase tracking-wide">Starred Chefs</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-warm-gray-50 to-warm-gray-100 rounded-sm">
                  <div className="text-3xl font-thin text-charcoal-900 mb-2">∞</div>
                  <div className="text-xs font-light text-charcoal-600 uppercase tracking-wide">Creativity</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter II - Bien-être Transcendant */}
      <section className="py-40 bg-gradient-to-b from-warm-gray-50 to-white relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Chapter Header */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="text-6xl font-thin text-charcoal-200 mr-8">02</div>
              <div>
                <div className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-2 uppercase">Chapter Two</div>
                <h2 className="text-5xl font-thin text-charcoal-900 tracking-tight">Transcendent Wellness</h2>
              </div>
            </div>
            <div className="w-full h-px bg-gradient-to-r from-charcoal-300 via-charcoal-100 to-transparent"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
            <div className="lg:col-span-5 space-y-10 order-2 lg:order-1">
              <div>
                <h3 className="text-4xl font-thin text-charcoal-900 mb-6 leading-tight">
                  Sanctuary<br />
                  <span className="text-3xl opacity-60">of the Soul</span>
                </h3>
                <p className="text-lg font-light text-charcoal-700 leading-relaxed mb-8">
                  Transform your yacht into a temple of serenity where body and spirit 
                  find their perfect balance to the rhythm of waves and wind.
                </p>
              </div>
              
              {/* Wellness Menu */}
              <div className="bg-white p-8 rounded-sm shadow-xl border border-warm-gray-100">
                <h4 className="text-lg font-light text-charcoal-900 mb-6 text-center">Signature Rituals</h4>
                <div className="space-y-4">
                  {[
                    { time: "06:00", ritual: "Sunrise Meditation", desc: "Facing the sunrise" },
                    { time: "10:00", ritual: "Yoga Flow", desc: "On the main deck" },
                    { time: "14:00", ritual: "Holistic Massage", desc: "With essential oils" },
                    { time: "19:00", ritual: "Sound Bath", desc: "Tibetan bowls under the stars" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-3 border-b border-warm-gray-100 last:border-b-0">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm font-light text-charcoal-500 w-12">{item.time}</div>
                        <div>
                          <div className="text-sm font-light text-charcoal-900">{item.ritual}</div>
                          <div className="text-xs font-light text-charcoal-600">{item.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-7 relative order-1 lg:order-2">
              <div className="relative group">
                <img 
                  src="https://images.unsplash.com/photo-1697124510322-27ef594f67fd"
                  alt="Bien-être transcendant"
                  className="w-full h-[600px] object-cover rounded-sm transition-transform duration-700 group-hover:scale-[1.02]"
                />
                
                {/* Floating Zen Quote */}
                <div className="absolute top-8 right-8 bg-white/90 backdrop-blur-sm p-6 rounded-sm border border-white/20 max-w-xs">
                  <blockquote className="text-sm font-light text-charcoal-700 italic leading-relaxed">
                    "Peace comes from within. Do not seek it without."
                  </blockquote>
                  <div className="text-xs font-light text-charcoal-500 mt-2">— Bouddha</div>
                </div>
                
                {/* Floating Credentials */}
                <div className="absolute bottom-8 left-8 bg-charcoal-900 text-white p-6 rounded-sm">
                  <div className="text-center">
                    <div className="text-2xl font-thin mb-2">🧘‍♀️</div>
                    <div className="text-xs font-light opacity-70 uppercase tracking-wide">Zen Master</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter III - Aventures Épiques */}
      <section className="py-40 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Chapter Header */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <div className="text-6xl font-thin text-charcoal-200 mr-8">03</div>
              <div>
                <div className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-2 uppercase">Chapter Three</div>
                <h2 className="text-5xl font-thin text-charcoal-900 tracking-tight">Epic Adventures</h2>
              </div>
            </div>
            <div className="w-full h-px bg-gradient-to-r from-charcoal-300 via-charcoal-100 to-transparent"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
            <div className="lg:col-span-7 relative">
              <div className="relative group">
                <img 
                  src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b"
                  alt="Aventures épiques"
                  className="w-full h-[600px] object-cover rounded-sm transition-transform duration-700 group-hover:scale-[1.02]"
                />
                
                {/* Equipment Arsenal Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-sm"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-sm p-8">
                    <h4 className="text-white text-lg font-light mb-6 text-center">Exclusive Arsenal</h4>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                      {[
                        { 
                          icon: (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 8v10a2 2 0 01-2 2H6a2 2 0 01-2-2V8"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2 8h20"/>
                            </svg>
                          ), 
                          count: "12+", 
                          type: "Jet Skis" 
                        },
                        { 
                          icon: (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.002 4.002 0 003 15z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v8"/>
                            </svg>
                          ), 
                          count: "6+", 
                          type: "Tenders" 
                        },
                        { 
                          icon: (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" strokeWidth="1.5"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h8"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v8"/>
                            </svg>
                          ), 
                          count: "Pro", 
                          type: "Diving" 
                        },
                        { 
                          icon: (
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          ), 
                          count: "1", 
                          type: "Helipad" 
                        }
                      ].map((item, index) => (
                        <div key={index} className="text-center text-white">
                          <div className="mb-3 flex justify-center">{item.icon}</div>
                          <div className="text-lg font-thin mb-1">{item.count}</div>
                          <div className="text-xs font-light opacity-70 uppercase tracking-wide">{item.type}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:col-span-5 space-y-10">
              <div>
                <h3 className="text-4xl font-thin text-charcoal-900 mb-6 leading-tight">
                  Intense<br />
                  <span className="text-3xl opacity-60">Emotions</span>
                </h3>
                <p className="text-lg font-light text-charcoal-700 leading-relaxed mb-8">
                  Awaken the adventurer within you with our collection 
                  of high-end equipment and breathtaking experiences.
                </p>
              </div>
              
              {/* Premium Activities Grid */}
              <div className="grid grid-cols-1 gap-6">
                {[
                  { 
                    title: "Free Diving", 
                    desc: "Exploration of virgin reefs with professional equipment",
                    level: "All levels",
                    duration: "2-4h"
                  },
                  { 
                    title: "Sport Fishing", 
                    desc: "Track noble species with high-end equipment",
                    level: "Expert",
                    duration: "4-8h"
                  },
                  { 
                    title: "Heli-Transfer", 
                    desc: "Exclusive aerial approach to secret destinations",
                    level: "Premium",
                    duration: "Bespoke"
                  }
                ].map((activity, index) => (
                  <div key={index} className="p-6 bg-gradient-to-r from-warm-gray-50 to-transparent border-l-2 border-charcoal-300 hover:border-charcoal-900 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-lg font-light text-charcoal-900">{activity.title}</h4>
                      <span className="text-xs font-light text-charcoal-500 bg-warm-gray-200 px-2 py-1 rounded uppercase tracking-wide">
                        {activity.level}
                      </span>
                    </div>
                    <p className="text-sm font-light text-charcoal-600 leading-relaxed mb-2">{activity.desc}</p>
                    <div className="text-xs font-light text-charcoal-500">{activity.duration}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Cinematic */}
      <section className="py-40 bg-gradient-to-b from-charcoal-900 via-black to-charcoal-900 text-white relative overflow-hidden">
        {/* Geometric Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
          <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
          <div className="absolute top-0 left-1/3 w-px h-full bg-gradient-to-b from-transparent via-white/5 to-transparent"></div>
          <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="max-w-6xl mx-auto px-6 text-center relative">
          {/* Ornamental Header */}
          <div className="mb-20">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent mx-auto mb-8"></div>
            <h2 className="text-xs font-light tracking-[0.5em] mb-6 uppercase opacity-70">Testimonials</h2>
            <h3 className="text-6xl font-thin mb-8 tracking-tight leading-none">
              MOMENTS<br />
              <span className="text-4xl opacity-60">OF ETERNITY</span>
            </h3>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent mx-auto"></div>
          </div>
          
          {/* Main Testimonial */}
          <div className="max-w-5xl mx-auto mb-20">
            <blockquote className="text-3xl lg:text-4xl font-light leading-relaxed mb-12 opacity-90">
              "PRIVIA Charter transcended our wildest expectations. 
              Every moment was orchestrated with goldsmith precision, 
              creating a symphony of emotions we will cherish forever."
            </blockquote>
            
            <div className="flex items-center justify-center space-x-6">
              <div className="w-20 h-px bg-gradient-to-r from-transparent to-white/30"></div>
              <div className="text-center">
                <div className="text-lg font-light mb-1">Philippe & Isabelle Moreau</div>
                <div className="text-sm font-light opacity-60">Entrepreneurs, Monaco</div>
              </div>
              <div className="w-20 h-px bg-gradient-to-l from-transparent to-white/30"></div>
            </div>
          </div>
          
          {/* Rating Constellation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { stars: 5, quote: "Transcendent service", author: "Alexandre D.", location: "Paris" },
              { stars: 5, quote: "Transformative experience", author: "Marina L.", location: "Milan" },
              { stars: 5, quote: "Luxury redefined", author: "Charles B.", location: "Geneva" }
            ].map((review, index) => (
              <div key={index} className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-sm"></div>
                <div className="relative p-8 border border-white/10 rounded-sm backdrop-blur-sm">
                  <div className="flex justify-center mb-6">
                    {[...Array(review.stars)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-white text-white mx-1" />
                    ))}
                  </div>
                  <blockquote className="text-lg font-light mb-6 opacity-90">"{review.quote}"</blockquote>
                  <div className="text-sm font-light opacity-60">
                    — {review.author}, {review.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - Cinematic */}
      <section className="py-40 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-warm-gray-50 via-white to-warm-gray-50"></div>
        
        <div className="max-w-6xl mx-auto px-6 text-center relative">
          {/* Ornamental Opening */}
          <div className="mb-16">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-charcoal-400 to-transparent mx-auto mb-8"></div>
            <div className="text-xs font-light tracking-[0.5em] text-charcoal-500 mb-6 uppercase">
              Ready for Excellence?
            </div>
          </div>
          
          <div className="space-y-12">
            <h2 className="text-7xl lg:text-8xl font-thin text-charcoal-900 tracking-tighter leading-none">
              LET'S CREATE<br />
              <span className="text-5xl lg:text-6xl opacity-60">YOUR</span><br />
              LEGEND
            </h2>
            
            <p className="text-2xl font-light text-charcoal-700 leading-relaxed max-w-4xl mx-auto">
              Let our master craftsmen transform your boldest dreams into reality. 
              Every detail will be chiseled, every moment sculpted for eternity.
            </p>
            
            <div className="flex flex-col lg:flex-row items-center justify-center space-y-6 lg:space-y-0 lg:space-x-12">
              <button className="group px-16 py-6 bg-charcoal-900 text-white text-sm font-light tracking-[0.2em] hover:bg-charcoal-800 transition-all duration-500 uppercase relative overflow-hidden">
                <span className="relative z-10">Create My Experience</span>
                <ArrowRight className="w-4 h-4 ml-3 inline-block group-hover:translate-x-2 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-r from-charcoal-800 to-charcoal-900 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
              </button>
              
              <div className="flex items-center space-x-4 text-charcoal-600">
                <Phone className="w-5 h-5" />
                <span className="text-lg font-light tracking-wide">+1 786 840 0745</span>
              </div>
            </div>
          </div>
          
          {/* Ornamental Closing */}
          <div className="mt-16">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-charcoal-400 to-transparent mx-auto"></div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page Component - Refined & Elegant with Translations
const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service: 'charter',
    preferred_contact: 'email',
    budget_range: '',
    charter_dates: '',
    // Honeypot field - invisible to users, only bots fill this
    website: '' // If filled, it's spam
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 🛡️ HONEYPOT VALIDATION - Block spam bots
      if (formData.website && formData.website.trim() !== '') {
        console.log('🚨 SPAM DETECTED: Honeypot field filled:', formData.website);
        alert('❌ Spam detected. Your submission has been blocked.');
        setIsSubmitting(false);
        return;
      }

      console.log('\n🚀 ===== FRONTEND CONTACT SUBMISSION =====');
      console.log('⏰ Timestamp:', new Date().toISOString());
      console.log('🛡️ Honeypot check: PASSED (website field empty)');
      console.log('📋 Contact data being sent:', JSON.stringify(formData, null, 2));
      console.log('🌐 API Endpoint:', `${API}/send-contact-emails`);

      const response = await fetch(`${API}/send-contact-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('📡 HTTP Response Status:', response.status, response.statusText);
      console.log('📋 Response Headers:', Object.fromEntries(response.headers.entries()));

      const result = await response.json();
      
      console.log('📨 API Response Body:', JSON.stringify(result, null, 2));

      if (result.success) {
        console.log('✅ ===== CONTACT SUCCESS =====');
        console.log('📧 Client Email ID:', result.details?.clientEmailId);
        console.log('📧 Internal Email ID:', result.details?.internalEmailId);
        console.log('⏱️ Processing Time:', result.details?.processingTime + 'ms');
        console.log('🎯 Success Message:', result.message);
        console.log('=======================================\n');
        
        alert(`✅ Your message has been sent successfully!

📧 Confirmation emails sent:
• Your confirmation: ${formData.email}
• Our team: chris@priviayachts.com

📋 Details:
• Message ID: ${result.details?.clientEmailId?.substring(0, 8)}...
• Processing time: ${result.details?.processingTime}ms

Our yacht charter specialist will contact you within 24 hours.`);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          service: 'charter',
          preferred_contact: 'email',
          budget_range: '',
          charter_dates: '',
          website: '' // Reset honeypot field
        });
        
      } else {
        console.error('❌ ===== CONTACT FAILED =====');
        console.error('💥 Error message:', result.message);
        console.error('🔍 Error details:', result.error);
        console.error('=====================================\n');
        
        throw new Error(result.message || 'Failed to send contact message');
      }
    } catch (error) {
      console.error('\n❌ ===== FRONTEND CONTACT ERROR =====');
      console.error('💥 Error type:', error.constructor.name);
      console.error('📝 Error message:', error.message);
      console.error('📚 Error stack:', error.stack);
      console.error('===============================\n');
      
      alert(`❌ There was an error sending your message.

Error details: ${error.message}

Please try again or contact us directly:
📞 +1 786 840 0745
📧 chris@priviayachts.com`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Section - Enhanced */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/photo-1567899378494-47b22a2ae96a.avif')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-6">
          <div className="mb-6">
            <div className="w-16 h-px bg-white/30 mx-auto mb-6"></div>
            <h1 className="text-xs font-light tracking-[0.4em] mb-6 opacity-90 uppercase">Contact Our Specialists</h1>
          </div>
          <h2 className="text-6xl lg:text-7xl font-thin leading-none mb-8 tracking-tight">
            YOUR JOURNEY<br />
            <span className="text-5xl lg:text-6xl opacity-80">STARTS HERE</span>
          </h2>
          <p className="text-xl font-light leading-relaxed opacity-80 max-w-3xl mx-auto">
            Connect with our expert yacht charter specialists to craft your perfect maritime escape. 
            From intimate getaways to grand celebrations, we orchestrate unforgettable experiences.
          </p>
          <div className="w-16 h-px bg-white/30 mx-auto mt-8"></div>
        </div>
      </section>

      {/* Contact Form & Info - Enhanced Layout */}
      <section className="py-32 bg-gradient-to-b from-white to-warm-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-20">
            {/* Contact Form - Enhanced */}
            <div className="lg:col-span-3">
              <div className="mb-12">
                <div className="w-12 h-px bg-charcoal-300 mb-6"></div>
                <h2 className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-4 uppercase">Get In Touch</h2>
                <h3 className="text-5xl font-thin text-charcoal-900 mb-6 leading-tight">Let's Create Your<br />Perfect Charter</h3>
                <p className="text-lg font-light text-charcoal-600 leading-relaxed max-w-2xl">
                  Share your vision with us, and our specialists will transform it into an extraordinary yachting experience tailored exclusively for you.
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-xs font-light text-charcoal-500 mb-3 uppercase tracking-[0.2em]">
                      Service Interest
                    </label>
                    <select 
                      className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light text-lg"
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                    >
                      <option value="charter">Luxury Yacht Charter</option>
                      <option value="purchase">Yacht Acquisition</option>
                      <option value="management">Yacht Management</option>
                      <option value="consultation">Private Consultation</option>
                      <option value="event">Special Events</option>
                      <option value="corporate">Corporate Charters</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-light text-charcoal-500 mb-3 uppercase tracking-[0.2em]">
                      Preferred Contact Method
                    </label>
                    <select 
                      className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light text-lg"
                      value={formData.preferred_contact}
                      onChange={(e) => setFormData({...formData, preferred_contact: e.target.value})}
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone Call</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="video">Video Call</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <input 
                      type="text"
                      placeholder="Full Name"
                      required
                      className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light placeholder-charcoal-400 text-lg"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>

                  <div>
                    <input 
                      type="email"
                      placeholder="Email Address"
                      required
                      className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light placeholder-charcoal-400 text-lg"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <input 
                      type="tel"
                      placeholder="Phone Number"
                      className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light placeholder-charcoal-400 text-lg"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>

                  <div>
                    <input 
                      type="text"
                      placeholder="Preferred Charter Dates (Optional)"
                      className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light placeholder-charcoal-400 text-lg"
                      value={formData.charter_dates}
                      onChange={(e) => setFormData({...formData, charter_dates: e.target.value})}
                    />
                  </div>
                </div>

                {/* 🛡️ HONEYPOT FIELD - Invisible to users, only bots fill this */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                  <input 
                    type="text"
                    name="website"
                    placeholder="Leave this field empty"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    aria-hidden="true"
                  />
                </div>

                <div>
                  <label className="block text-xs font-light text-charcoal-500 mb-3 uppercase tracking-[0.2em]">
                    Budget Range (Optional)
                  </label>
                  <select 
                    className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light text-lg"
                    value={formData.budget_range}
                    onChange={(e) => setFormData({...formData, budget_range: e.target.value})}
                  >
                    <option value="">Please select a range</option>
                    <option value="50k-100k">$50K - $100K per week</option>
                    <option value="100k-250k">$100K - $250K per week</option>
                    <option value="250k-500k">$250K - $500K per week</option>
                    <option value="500k-1m">$500K - $1M per week</option>
                    <option value="1m+">$1M+ per week</option>
                    <option value="flexible">Flexible / Discuss</option>
                  </select>
                </div>

                <div>
                  <textarea 
                    rows="6"
                    placeholder="Tell us about your dream charter experience... destinations, group size, special occasions, preferences, or any specific requirements."
                    required
                    className="w-full p-5 border-0 border-b-2 border-warm-gray-200 bg-transparent text-charcoal-900 focus:outline-none focus:border-charcoal-400 transition-all duration-300 font-light placeholder-charcoal-400 resize-none text-lg leading-relaxed"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>

                <div className="pt-8">
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full py-6 bg-charcoal-900 text-white text-sm font-light tracking-[0.2em] hover:bg-charcoal-800 transition-all duration-500 uppercase overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="relative z-10">
                      {isSubmitting ? 'Sending Your Message...' : 'Send Your Message'}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-charcoal-800 to-charcoal-700 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information - Enhanced */}
            <div className="lg:col-span-2 space-y-16">
              <div>
                <div className="w-12 h-px bg-charcoal-300 mb-6"></div>
                <h3 className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-8 uppercase">Our Office</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-2xl font-light text-charcoal-900 mb-4">Saint-Barthélemy</h4>
                    <p className="text-lg font-light text-charcoal-700 leading-relaxed">
                      Gustavia Harbor<br />
                      Rue de la République<br />
                      97133 Saint-Barthélemy<br />
                      French West Indies
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-8 uppercase">Direct Contact</h4>
                <div className="space-y-6">
                  <div className="group">
                    <div className="flex items-center space-x-4 p-4 rounded-sm hover:bg-warm-gray-50 transition-colors duration-300">
                      <div className="w-10 h-10 bg-charcoal-100 rounded-full flex items-center justify-center group-hover:bg-charcoal-200 transition-colors duration-300">
                        <Phone className="w-4 h-4 text-charcoal-600" />
                      </div>
                      <div>
                        <p className="text-xs font-light text-charcoal-500 uppercase tracking-wide mb-1">Phone</p>
                        <p className="text-lg font-light text-charcoal-900">+1 786 840 0745</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group">
                    <div className="flex items-center space-x-4 p-4 rounded-sm hover:bg-warm-gray-50 transition-colors duration-300">
                      <div className="w-10 h-10 bg-charcoal-100 rounded-full flex items-center justify-center group-hover:bg-charcoal-200 transition-colors duration-300">
                        <Mail className="w-4 h-4 text-charcoal-600" />
                      </div>
                      <div>
                        <p className="text-xs font-light text-charcoal-500 uppercase tracking-wide mb-1">Email</p>
                        <p className="text-lg font-light text-charcoal-900">chris@priviayachts.com</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-light tracking-[0.3em] text-charcoal-500 mb-8 uppercase">Office Hours</h4>
                <div className="space-y-4 text-lg font-light text-charcoal-700">
                  <div className="flex justify-between items-center py-2 border-b border-warm-gray-100">
                    <span>Monday - Friday</span>
                    <span className="text-charcoal-500">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-warm-gray-100">
                    <span>Saturday</span>
                    <span className="text-charcoal-500">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-warm-gray-100">
                    <span>Sunday</span>
                    <span className="text-charcoal-500">By Appointment</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-charcoal-50 to-warm-gray-50 p-8 rounded-sm">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-red-600" />
                  </div>
                  <h4 className="text-lg font-light text-charcoal-900">24/7 Emergency Support</h4>
                </div>
                <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                  For our clients currently at sea or urgent charter needs:<br />
                  <span className="font-medium text-charcoal-900">+1 786 840 0745</span>
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-sm">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-light text-charcoal-900">Instant Response</h4>
                </div>
                <p className="text-sm font-light text-charcoal-600 leading-relaxed">
                  Need immediate assistance? Connect with us instantly via WhatsApp for real-time support and quick answers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section - Enhanced */}
      <section className="h-[50vh] bg-gradient-to-b from-warm-gray-100 to-warm-gray-200 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/10 to-transparent"></div>
        <div className="w-full h-full flex items-center justify-center relative z-10">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-6 h-6 text-charcoal-600" />
            </div>
            <h4 className="text-2xl font-light text-charcoal-900 mb-3">Gustavia Harbor</h4>
            <p className="text-sm font-light text-charcoal-600 leading-relaxed">
              Interactive map and virtual office tour<br />
              <span className="opacity-75">Coming soon</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Content Component (inside Router context) - Optimized
const AppContent = () => {
  const navigate = useNavigate();
  const [selectedYacht, setSelectedYacht] = useState(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Remove global yacht loading - each page will load its own data when needed

  const handleBookNow = (yacht) => {
    setSelectedYacht(yacht);
    setIsBookingModalOpen(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      await axios.post(`${API}/bookings`, bookingData);
      alert('Votre demande de réservation a été envoyée avec succès. Nous vous contacterons bientôt.');
      setIsBookingModalOpen(false);
      setSelectedYacht(null);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  // No global loading state needed - each page handles its own loading

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={
            <div className="min-h-screen bg-white">
              <Header onMenuClick={() => setIsMenuOpen(!isMenuOpen)} isMenuOpen={isMenuOpen} isTransparent={true} />
              
              <Hero />
              
              <SearchSection />
              
              {/* Destinations Section - Refined PRIVIA Style */}
              <section className="py-24 bg-charcoal-900 text-white">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="text-center mb-16">
                    <h2 className="text-sm font-light tracking-[0.3em] mb-4 uppercase opacity-80">Explore</h2>
                    <h3 className="text-5xl font-thin mb-6 tracking-tight">DESTINATIONS.</h3>
                    <p className="text-lg font-light opacity-70 leading-relaxed max-w-3xl mx-auto">
                      Create your perfect escape by choosing from the most beautiful yachting destinations. 
                      Let us craft your personalized itinerary in the most exclusive havens.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {/* Monaco */}
                    <div className="group cursor-pointer" onClick={() => navigate('/destination/monaco')}>
                      <div className="relative overflow-hidden rounded-sm h-80">
                        <img 
                          src="https://images.unsplash.com/photo-1617330666625-e75bee2e333b"
                          alt="Monaco"
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="mb-2">
                            <span className="text-xs font-light tracking-[0.3em] uppercase opacity-80 mb-1 block">Principality</span>
                            <h4 className="text-xl font-thin tracking-wide mb-1">Monaco</h4>
                            <p className="text-xs font-light opacity-70 leading-relaxed mb-2">
                              Absolute prestige in the heart of the French Riviera.
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-light opacity-60 uppercase tracking-wide">
                              May - Sep
                            </div>
                            <div className="w-6 h-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Saint-Tropez */}
                    <div className="group cursor-pointer" onClick={() => navigate('/destination/saint-tropez')}>
                      <div className="relative overflow-hidden rounded-sm h-80">
                        <img 
                          src="https://images.unsplash.com/photo-1669428521191-5aef429aa1e5"
                          alt="Saint-Tropez"
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="mb-2">
                            <span className="text-xs font-light tracking-[0.3em] uppercase opacity-80 mb-1 block">Riviera</span>
                            <h4 className="text-xl font-thin tracking-wide mb-1">Saint-Tropez</h4>
                            <p className="text-xs font-light opacity-70 leading-relaxed mb-2">
                              Refined Mediterranean art of living.
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-light opacity-60 uppercase tracking-wide">
                              Jun - Sep
                            </div>
                            <div className="w-6 h-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Greek Islands */}
                    <div className="group cursor-pointer" onClick={() => navigate('/destination/greek-islands')}>
                      <div className="relative overflow-hidden rounded-sm h-80">
                        <img 
                          src="https://images.unsplash.com/photo-1664268406930-fa8a02c6e09f"
                          alt="Greek Islands"
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="mb-2">
                            <span className="text-xs font-light tracking-[0.3em] uppercase opacity-80 mb-1 block">Cyclades</span>
                            <h4 className="text-xl font-thin tracking-wide mb-1">Greek Islands</h4>
                            <p className="text-xs font-light opacity-70 leading-relaxed mb-2">
                              Mykonos, Santorini... Eternal splendor.
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-light opacity-60 uppercase tracking-wide">
                              Apr - Oct
                            </div>
                            <div className="w-6 h-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Caribbean */}
                    <div className="group cursor-pointer" onClick={() => navigate('/destination/caribbean')}>
                      <div className="relative overflow-hidden rounded-sm h-80">
                        <img 
                          src="https://images.unsplash.com/photo-1719664026730-85137c88c7b9"
                          alt="Caribbean"
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="mb-2">
                            <span className="text-xs font-light tracking-[0.3em] uppercase opacity-80 mb-1 block">Tropical</span>
                            <h4 className="text-xl font-thin tracking-wide mb-1">Caribbean</h4>
                            <p className="text-xs font-light opacity-70 leading-relaxed mb-2">
                              St. Barts, turquoise waters.
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-light opacity-60 uppercase tracking-wide">
                              Dec - Apr
                            </div>
                            <div className="w-6 h-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Amalfi Coast */}
                    <div className="group cursor-pointer" onClick={() => navigate('/destination/amalfi')}>
                      <div className="relative overflow-hidden rounded-sm h-80">
                        <img 
                          src="https://images.unsplash.com/photo-1594453987501-8ae427f3243b"
                          alt="Amalfi Coast"
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="mb-2">
                            <span className="text-xs font-light tracking-[0.3em] uppercase opacity-80 mb-1 block">Italy</span>
                            <h4 className="text-xl font-thin tracking-wide mb-1">Amalfi</h4>
                            <p className="text-xs font-light opacity-70 leading-relaxed mb-2">
                              Positano, Capri... Eternal romance.
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-light opacity-60 uppercase tracking-wide">
                              May - Oct
                            </div>
                            <div className="w-6 h-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ibiza */}
                    <div className="group cursor-pointer" onClick={() => navigate('/destination/ibiza')}>
                      <div className="relative overflow-hidden rounded-sm h-80">
                        <img 
                          src="https://images.pexels.com/photos/3834352/pexels-photo-3834352.jpeg"
                          alt="Ibiza"
                          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="mb-2">
                            <span className="text-xs font-light tracking-[0.3em] uppercase opacity-80 mb-1 block">Balearic</span>
                            <h4 className="text-xl font-thin tracking-wide mb-1">Ibiza</h4>
                            <p className="text-xs font-light opacity-70 leading-relaxed mb-2">
                              Electrifying energy, white island.
                            </p>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-light opacity-60 uppercase tracking-wide">
                              Jun - Sep
                            </div>
                            <div className="w-6 h-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="text-center mt-16">
                    <button 
                      className="px-8 py-4 border border-white/30 text-white text-sm font-light tracking-wide hover:bg-white hover:text-charcoal-900 transition-all duration-500 backdrop-blur-sm uppercase"
                      onClick={() => navigate('/escapes')}
                    >
                      Discover All Destinations
                    </button>
                  </div>
                </div>
              </section>
              
              {/* Featured Yachts Section - Optimized with limited selection */}
              <section id="featured-yachts" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="text-center mb-20">
                    <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Our Fleet</h2>
                    <h3 className="text-5xl font-thin text-charcoal-900 mb-6 tracking-tight">Featured Yachts</h3>
                    <p className="text-lg font-light text-charcoal-600 leading-relaxed max-w-3xl mx-auto">
                      Our luxury yacht charter fleet features motor yachts, sailing yachts, and superyachts 
                      available for charter in Mediterranean and Caribbean destinations with professional crew.
                    </p>
                  </div>
                  
                  <FeaturedYachtsSection onBookNow={handleBookNow} />
                  
                  {/* Call to Action */}
                  <div className="text-center mt-16">
                    <button 
                      onClick={() => navigate('/collection')}
                      className="px-8 py-4 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all duration-300 uppercase"
                    >
                      View All Yachts (60 per page)
                    </button>
                  </div>
                </div>
              </section>
              
              <footer className="bg-charcoal-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
                    <div>
                      <h3 
                        className="text-2xl font-light tracking-wide mb-6 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => navigate('/')}
                      >
                        PRIVIA<span className="font-thin">CHARTER</span>
                      </h3>
                      <p className="text-sm font-light text-white/70 leading-relaxed mb-6">
                        The art of escape for privileged souls seeking more than luxury.
                      </p>
                      <div className="space-y-4 text-sm font-light text-white/70">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-3" />
                          +1 786 840 0745
                        </div>
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-3" />
                          contact@priviacharter.com
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-xs font-light tracking-[0.3em] mb-6 uppercase text-white/80">Destinations</h4>
                      <ul className="space-y-3 text-sm font-light text-white/70">
                        <li>
                          <a href="/destination/caribbean" className="hover:text-white transition-colors duration-300">
                            Caribbean
                          </a>
                        </li>
                        <li>
                          <a href="/destination/monaco" className="hover:text-white transition-colors duration-300">
                            Monaco
                          </a>
                        </li>
                        <li>
                          <a href="/destination/saint-tropez" className="hover:text-white transition-colors duration-300">
                            Saint-Tropez
                          </a>
                        </li>
                        <li>
                          <a href="/destination/greek-islands" className="hover:text-white transition-colors duration-300">
                            Greek Islands
                          </a>
                        </li>
                        <li>
                          <a href="/destination/amalfi" className="hover:text-white transition-colors duration-300">
                            Amalfi Coast
                          </a>
                        </li>
                        <li>
                          <a href="/destination/ibiza" className="hover:text-white transition-colors duration-300">
                            Ibiza
                          </a>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-xs font-light tracking-[0.3em] mb-6 uppercase text-white/80">Services</h4>
                      <ul className="space-y-3 text-sm font-light text-white/70">
                        <li>
                          <a href="/services/private-escapes" className="hover:text-white transition-colors duration-300">
                            Private Escapes
                          </a>
                        </li>
                        <li>
                          <a href="/services/exclusive-collection" className="hover:text-white transition-colors duration-300">
                            Exclusive Collection
                          </a>
                        </li>
                        <li>
                          <a href="/services/bespoke-experiences" className="hover:text-white transition-colors duration-300">
                            Bespoke Experiences
                          </a>
                        </li>
                        <li>
                          <a href="/services/maritime-concierge" className="hover:text-white transition-colors duration-300">
                            Maritime Concierge
                          </a>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-xs font-light tracking-[0.3em] mb-6 uppercase text-white/80">Navigation</h4>
                      <ul className="space-y-3 text-sm font-light text-white/70">
                        <li>
                          <a href="/escapes" className="hover:text-white transition-colors duration-300">
                            Escapes
                          </a>
                        </li>
                        <li>
                          <a href="/collection" className="hover:text-white transition-colors duration-300">
                            Collection
                          </a>
                        </li>
                        <li>
                          <a href="/experience" className="hover:text-white transition-colors duration-300">
                            Experience
                          </a>
                        </li>
                        <li>
                          <a href="/contact" className="hover:text-white transition-colors duration-300">
                            Contact
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="border-t border-white/10 mt-12 pt-8 text-center">
                    <p className="text-xs font-light text-white/50 tracking-wide">
                      © 2024 PRIVIA CHARTER. All rights reserved.
                    </p>
                  </div>
                </div>
              </footer>
              
              {selectedYacht && (
                <BookingModal 
                  yacht={selectedYacht}
                  isOpen={isBookingModalOpen}
                  onClose={() => {
                    setIsBookingModalOpen(false);
                    setSelectedYacht(null);
                  }}
                  onSubmit={handleBookingSubmit}
                />
              )}
            </div>
          } />
          <Route path="/escapes" element={<EscapesPage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/experience" element={<ExperiencePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/yacht/:id" element={<YachtDetail onBookNow={handleBookNow} />} />
          <Route path="/yacht/:id/:slug" element={<YachtDetail onBookNow={handleBookNow} />} />
          <Route path="/destination/:destinationId" element={<DestinationPage onBookNow={handleBookNow} />} />
          <Route path="/services/:serviceId" element={<ServicePage />} />
        </Routes>
      
      {selectedYacht && (
        <BookingModal 
          yacht={selectedYacht}
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedYacht(null);
          }}
          onSubmit={handleBookingSubmit}
        />
      )}
      
      {/* WhatsApp Floating Bar - Available on all pages */}
      <WhatsAppBar />
    </div>
  );
};

// Main App Component Wrapper
const App = () => {
  // Initialiser le gestionnaire d'erreurs au démarrage
  useEffect(() => {
    setupErrorHandling();
    detectProblematicExtensions();
  }, []);

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
};

export default App;